// **The sandbox screen** (`client-sandbox-screen`, tasks 6.0–6.3; design **T1**, design doc §3).
//
// Rounds mode's whole editing loop on one screen: watch → diagnose → edit/prompt → run → watch. A
// world view showing the selected run under a playhead, a control stack of exactly three bars under
// it, and two popups over the top.
//
// ## This file is wiring, and that is the design
//
// `ui/screens/Colony.tsx` is 2147 lines against a 71 KB test file. That is the price of putting a
// screen's behaviour inside a component, where the only way to reach it is through a DOM. Design
// **T1** / **T2** say not to pay it twice, so:
//
//   * **No branch, no timer and no request sequencing lives here.** Selecting a version is a
//     compound activate + load; a run writes itself over seconds while the player keeps editing; a
//     Beekeeper turn can settle in the middle of either. All of it — intent epochs, stale-result
//     rejection, partial failure, unmount cancellation — is `ui/rounds/sandbox-coordinator.ts`'s,
//     and this file dispatches events at it and reads the state back.
//   * **Every number drawn comes from a pure model**: `sandbox-layout.ts` (the portrait budget),
//     `type-counts.ts` (chip counts at the playhead), `run-submit-model.ts` (the transport's six
//     states), `written-extent.ts` (the track), `score-delta.ts` (the score's comparison),
//     `review-model.ts` (the score's supporting stats and its caption), `review-world.ts` (the
//     trace → renderer mapping, through `ReviewFrame`).
//
// What is genuinely local is UI state that has no meaning off-screen: which type's sheet is open,
// what the player has typed but not sent, whether the prompt holds focus, whether the score
// popover is showing. None of it is asynchronous and none of it can be stale.
//
// **The playhead is not among them.** Both transport gestures dispatch: pressing the control sends
// `playback-toggled`, and a drag that `PlaybackBar` resolves to a tick sends `seek`. The coordinator
// decides what each means — a seek is re-adjudicated against the written extent there, and a press
// at the terminal tick restarts rather than doing nothing — because it holds the loaded trace's
// span and this file holds nothing. `onSeek`/`onTogglePlayback` remain as the host's *notification*
// that the gesture happened; they are not the path playback changes by, and a mount that omits them
// is fully playable.
//
// The selected **speed step** is the one piece of local state that moved out: the frame loop that
// consumes it is the composition root's, so `RoundsController` owns the step and passes it back
// down. The prop is optional and the screen falls back to holding it, so a bare mount's ladder
// still highlights.
//
// ## The layout is arithmetic, not flex
//
// The spec's requirement is that the world view takes **all** the height the control stack leaves,
// and that focusing the prompt makes the world **give up** exactly the height the stack takes. Both
// are asserted, and jsdom measures nothing — so `sandbox-layout.ts` computes the budget and this
// file applies it as declared pixel heights. The frame height arrives as a prop rather than being
// measured here: the composition root knows the viewport, and a host that measures is a host that
// needs a `ResizeObserver`, which is a subscription, which is the thing this file does not own.
//
// ## Seams with no defaults
//
// `createRenderer` has **no default** — the `ReviewFrame` rule (`ColonyUnderlaySeams`' precedent):
// a real `CanvasRenderer` wants a GPU context, so a bare mount and every jsdom test render the
// screen's chrome with no canvas at all rather than constructing Pixi and hoping. `chat`,
// `versionSelector` and `buildCandidate` are the same shape: absent means the affordance is not
// offered, never that it is offered and silently does nothing.
//
// ## Two deliberate departures from the golden reference
//
//  1. **The score's info affordance is 44 × 44 beside the pill, not the kit's 18 × 18 inside it.**
//     `ResourceCounter`'s pill is a closed box — fixed at 136 px for five digits, with no trailing
//     slot — so the control has to render as a sibling regardless. Once it is outside, the kit's
//     hit-target exemption (visual spec §0.3) buys nothing, and a full 44 px target costs the pill's
//     fixed width nothing.
//  2. **The scope pill's ✕ stays 22 × 22**, which visual spec §0.3 exempts explicitly: it is the
//     secondary control of a 30 px pill inside a 44 px field, and growing it would push the field
//     past the row height the whole bar is budgeted from.
//
// The scope pill *row above the player's bubble* (visual spec §8) is `ChatOverlay`'s to draw. This
// screen's obligation is that the scope is **recorded with the message** — it travels to the seam
// as the message's target type — and that the open surface names it above the transcript.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
} from 'react';
import {
  FirstRunGuide,
  ROUNDS_GUIDE_STEPS,
} from '../components/onboarding/FirstRunGuide';
import {
  createOnboardingSeenStore,
  ROUNDS_ONBOARDING_SEEN_KEY,
  type OnboardingSeenStore,
} from '../../app/onboarding-store';
import { useClientStore } from '../../state/store';
import type { ReviewRun } from '../../net/rounds-review-source';
import { COLONY_CLOSE_SCALE, screenToWorld } from '../../canvas/projection';
import { pickEntityAt } from '../../canvas/pick';
import { reviewWorldSnapshot } from '../rounds/review-world';
import {
  cameraForZoomCentre,
  panByScreenDelta,
  zoomAboutScreenPoint,
  type CameraGestureBounds,
} from '../camera/camera-gestures';
import {
  useCanvasCameraGestures,
  type CanvasCameraGestureHandlers,
} from '../camera/use-canvas-camera-gestures';
import { ReviewCameraControl } from '../components/rounds/ReviewCameraControl';
import { PickedBeeChip } from '../components/rounds/PickedBeeChip';
import { detailHoldTicks, frameTickFor } from '../rounds/detail-continuity';
import { SIM_TICK_RATE_HZ } from '../../generated/sim-constants';
import { ChatOverlay } from '../components/chat/ChatOverlay';
import { BeeTypeBar } from '../components/hud/BeeTypeBar';
import { replaceTypeSlice, typeAtLine } from '../colony/document-slice';
import { PhaseClock } from '../components/hud/PhaseClock';
import { PlaybackBar, type ScriptDiagnostic } from '../components/hud/PlaybackBar';
import { COUNTER_PILL_WIDTH, ResourceCounter } from '../components/hud/ResourceCounter';
import { HUD_ICONS } from '../components/hud/hud-icons';
import {
  ReviewFrame,
  colonyFitZoom,
  hiveCentreOf,
  type ReviewRenderer,
} from '../components/rounds/ReviewFrame';
import { TypeSheet } from '../components/script/TypeSheet';
import { useChatSeam, isChatBusy, type ChatPhase, type ChatSeam } from '../colony/chat-seam';
import { PromptBar, PROMPT_BAR_MAX_WIDTH, type SandboxPromptLock } from '../components/hud/PromptBar';
import { summarizeThread } from '../chat/transcript-model';

// `SandboxPromptLock` and the bar itself moved to `ui/components/hud/PromptBar.tsx` so the pvp
// Colony screen can mount the same bar; re-exported here so this screen stays its callers' import.
export type { SandboxPromptLock };
import type { RunCandidate } from '../colony/run-controller';
import {
  useRoundsPhase,
  type RoundsPhaseSnapshot,
  type RoundsPhaseSource,
} from '../../app/rounds/rounds-phase';
import type { SelectionOutcome, SubmissionReadDto } from '../../net/rounds-http';
import { clockLabel, SCORE_HEADING, scoreStatRows } from '../rounds/review-model';
import {
  DEFAULT_SANDBOX_SPEED,
  transportStateFor,
  type SandboxSpeed,
  type TransportActivation,
} from '../rounds/run-submit-model';
import {
  COUNTER_STACK_TOP_PX,
  PROMPT_GROWTH_TRANSITION,
  REFERENCE_FRAME_WIDTH,
  sandboxHudLayout,
  sandboxLayout,
  sandboxLayoutVars,
  type SandboxLayout,
} from '../rounds/sandbox-layout';
import {
  transportInputFor,
  watchableTrace,
  writtenExtentFor,
  type SandboxCoordinator,
  type SandboxState,
} from '../rounds/sandbox-coordinator';
import { scoreComparison } from '../rounds/score-delta';
import { playheadPopulation, countForType } from '../rounds/type-counts';
import { playheadPoolCapacity } from '../rounds/pool-capacity';
import { abbrevForType, colorForType, spriteForType } from '../rounds/type-visuals';
import { waitingStateFor, type WaitingState } from '../rounds/waiting-state';
import { Button } from '../components/core/Button';
import { selectionAffordanceFor, type SelectionAffordance } from '../rounds/selection-model';
import { requestSelection } from '../rounds/selection-request';

/**
 * This screen's subject colony — the one the counters, the chips and `ReviewFrame`'s camera all
 * read. **Not** "a sandbox run has one colony": design **U1** lets a sandbox run be configured
 * with bot opponents, so the run itself may contain several. What is always true is narrower — the
 * player watches *their own* colony, and in their own run that is always colony 0 — so this is a
 * subject, not a field census. Named rather than inlined so a future run configured with more than
 * one *human* subject (none exists yet) is a one-line, greppable change.
 */
const SANDBOX_SUBJECT_COLONY = 0;

/** The default once-per-browser rounds-guide "seen" adapter (design D-Persist), backed by
 * `window.localStorage` under the rounds-specific key so it never collides with the live
 * Colony guide's record. Mirrors `Colony.tsx`'s own `WINDOW_ONBOARDING_SEEN_STORE`: the shim
 * references `window` lazily and every access is guarded inside the adapter, so constructing
 * it at module load is safe even where `window`/`localStorage` is unavailable. Tests inject an
 * in-memory double via the `onboardingSeenStore` prop instead. */
const WINDOW_ROUNDS_ONBOARDING_SEEN_STORE: OnboardingSeenStore = createOnboardingSeenStore(
  {
    getItem: (key) => window.localStorage.getItem(key),
    setItem: (key, value) => window.localStorage.setItem(key, value),
    removeItem: (key) => window.localStorage.removeItem(key),
  },
  ROUNDS_ONBOARDING_SEEN_KEY,
);

/**
 * The world camera's zoom range and rail step, matching the review screen this consolidated one
 * replaced. The floor sits below the whole-field establishing scale (0.2 shows a 4096 u field in
 * ~820 px) so a player can always pull back off the colony; the ceiling matches the Match screen's.
 */
const SANDBOX_ZOOM_MIN = 0.08;
const SANDBOX_ZOOM_MAX = 4;
const SANDBOX_ZOOM_BOUNDS: CameraGestureBounds = {
  minScale: SANDBOX_ZOOM_MIN,
  maxScale: SANDBOX_ZOOM_MAX,
};

/**
 * The world container's measured pixel size, or `null` before layout has happened (jsdom, or the
 * first paint before the ref resolves) — in which case the caller falls back to the declared frame.
 *
 * Measuring is not a refinement here, it is the fix: `cameraForZoomCentre`'s offset is a function
 * of container size, so deriving the camera from the 390 px reference width while the element was
 * actually the full window put the colony (measured − 390) / 2 px left of centre.
 */
function measuredSize(el: HTMLElement | null): { width: number; height: number } | null {
  if (el === null) {
    return null;
  }
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0 ? { width: rect.width, height: rect.height } : null;
}

/** One bee type as the bar, the sheet and the prompt's scope all need it. */
export interface SandboxTypeView {
  /** `ColonyTypeId` — what a trace record's per-bee identity is keyed on. */
  typeId: number;
  /** The type's own name, as the document's `type <name>` header spells it. */
  name: string;
  /** The buffed characteristic, for the sheet's header. */
  buff?: string | null;
  /** This type is the colony's birth type. */
  birthType?: boolean;
  /** The chip's alert dot — a compile error in this type's slice, or heavy losses. */
  alert?: boolean;
}

/** A produced candidate offered in the sandbox's separate G7 selection surface. */
export interface SandboxSelectionCandidate {
  /** The run-minted identity; selection never reads editor source or reconstructs a document. */
  candidateId: number;
  /** Player-set label, where the run supplied one. */
  label?: string | null;
  /** Makes a contested sandbox run legible without comparing its score to a solo one. */
  botOpponents?: boolean;
}

/**
 * **The practice framing** (`client-sandbox-screen`, `client-practice`; sketch
 * `practice-framing.html` panel B).
 *
 * Its **presence is the fork**, the same substitution `RoundsController` already makes at the route
 * level: absent, this screen is exactly the tournament screen it was, and every phase-chrome read
 * below is untouched. Present, the controls that exist only because a tournament exists are rendered
 * **absent** rather than disabled — a disabled Submit asserts that a tournament exists somewhere and
 * is momentarily unavailable, which is false here and would leave a player looking for a tournament
 * they are not in.
 *
 * The slot a tournament uses for its phase clock carries this instead, so the screen states what it
 * is rather than being defined by what is missing.
 */
export interface SandboxPracticeFraming {
  /** The identifier for the vacated phase-clock slot. */
  label: string;
  /** The run's configuration, as a second, quieter line — `5 rivals · seed B47C4`. `null` renders
   * the label alone rather than an empty pill. */
  detail: string | null;
}

/** The caller's result for the round the review phase just landed. `RoundsController` derives
 * this from the standings source and the D4 colony-placement model; this screen only presents it. */
export interface SandboxReviewResult {
  round: number;
  placement: number | null;
  score: number | null;
  shared: boolean;
  /** Detail retention affects replay fidelity, never this scored result or whether it opens. */
  detailEvicted: boolean;
}

export interface SandboxScreenProps {
  /** The coordinator. **All** asynchronous sequencing is its; this screen dispatches and reads. */
  coordinator: SandboxCoordinator;
  /** The colony's bee types, in document order — the order the extension palette is indexed by. */
  types?: readonly SandboxTypeView[];
  /**
   * The selected run's scored identity, where it has one. `null`/absent renders **two** counter
   * pills rather than a score pill showing nothing (visual spec §2.4): a run that has not produced
   * a score has no score, and a placeholder in a score's shape reads as one.
   */
  run?: ReviewRun | null;
  /** The player's previous run's score, or `null` on a first run — a first run shows **no** delta
   * rather than a comparison against zero, which would read as a collapse from a run that never
   * happened. */
  previousScore?: number | null;
  /** The Beekeeper chat seam. Absent (a bare mount) means the prompt has nowhere to send. */
  chat?: ChatSeam;
  /** The version selector, given the playback row's one flexing cell. A slot, because the selector
   * owns its own menu state and its rows come from a model this screen does not assemble. */
  versionSelector?: ReactNode;
  /** Candidates in the version selector's provenance order. This screen never score-sorts them. */
  selectionCandidates?: readonly SandboxSelectionCandidate[];
  /** The narrow selection write seam. It receives only the candidate identity. */
  markSelection?: (candidateId: number) => Promise<SelectionOutcome>;
  /** Optional rounds-chrome route seam. The root owns navigation and retains sandbox state. */
  onOpenStandings?: () => void;
  /** The Review-phase card's already-derived own result. Absent while the standings read lands. */
  reviewResult?: SandboxReviewResult | null;
  /** Explicit graded-review entry. This screen never navigates as a phase or result changes. */
  onWatchGradedRound?: (round: number) => void;
  /** Route-persisted G9 suppression, hoisted by the rounds root so a standings trip cannot
   * resurrect a notice already dismissed for this player and round. */
  dismissedDefaultingRounds?: ReadonlySet<number>;
  onDismissDefaultingRound?: (round: number) => void;
  /** The world renderer's factory. **No default**: omitted renders the chrome with no canvas. */
  createRenderer?: (container: HTMLElement, tickRateHz: number) => ReviewRenderer;
  /** The working document → the whole-colony candidate a Run submits (**G7**). Absent means this
   * mount cannot run, which is a wiring state, not a refusal to explain one. */
  buildCandidate?: (source: string) => RunCandidate | null;
  /**
   * The selected speed step, and the host's notification that it changed. Omitted, the screen holds
   * the step itself so a bare mount's ladder still works; `RoundsController` supplies both, because
   * the **frame loop** needs the step and the loop is the composition root's (design **T2**).
   */
  speed?: SandboxSpeed;
  onSpeed?: (step: SandboxSpeed) => void;
  /**
   * The steps that can apply right now, forwarded verbatim to the playback bar. Omitted means all of
   * them, so a bare mount's ladder is unrestricted.
   *
   * The screen computes nothing here: which steps a live watch window can sustain is a fact about the
   * run driver's window and the loop's rate, and both of those belong to the composition root for the
   * same reason the step itself does.
   */
  admissibleSpeeds?: readonly SandboxSpeed[];
  /**
   * The transport was pressed in `playing`/`paused`. The screen has **already dispatched**
   * `playback-toggled` by the time this fires; it is the host's notification, not the path
   * playback changes by — there is exactly one owner of `playback`, and it is the coordinator.
   */
  onTogglePlayback?: () => void;
  /**
   * A drag that resolved to a tick, after the screen has dispatched `seek` for it. Same shape and
   * same reason as {@link onTogglePlayback}: the coordinator adjudicates the seek against the
   * written extent and moves the playhead, and a host that wants to know it happened hears it here.
   */
  onSeek?: (tick: number) => void;
  /** The create-type control. Omitted renders no `+`. */
  onCreateType?: () => void;
  /** The host's height. Defaults to the 390 × 844 reference frame. */
  frameHeight?: number;
  /** The host's width, for the world camera. Defaults to the reference frame's. */
  frameWidth?: number;
  /**
   * The rounds phase surface (`client-rounds-phase`, design **D1** and **D5**): drives the G5
   * phase clock's corner pill, the popup headers' bare remainder, and the three waiting states'
   * selection (`ui/rounds/waiting-state.ts`). **Optional, with an inert default** — the shipped
   * seam convention `useRoundsPhase` itself states: omitted (a bare mount, or live mode, which
   * never constructs one) renders no phase chrome at all, never a placeholder countdown or an
   * invented phase name. The composition root wires the real channel in a later task group.
   */
  phase?: RoundsPhaseSource;
  /**
   * The practice framing. **Presence is the fork** — see {@link SandboxPracticeFraming}. Absent,
   * this screen renders a tournament round exactly as it did.
   */
  practice?: SandboxPracticeFraming | null;
  /** The prompt's code-gated state. Absent, the prompt behaves as it always has. */
  promptLock?: SandboxPromptLock | null;
  /**
   * **A draft was saved and its verdict is the device's to reach** (task 5.12).
   *
   * A host *notification*, in exactly the sense {@link onTogglePlayback} and {@link onSeek} are: the
   * screen has already dispatched `document-edited` by the time this fires, and the host answers by
   * dispatching `draft-compiled` when its compile lands. The screen holds nothing across time — the
   * compile is asynchronous and sequencing it here would make this module a third owner of when things
   * happen (`SandboxScreen.host.test.tsx` asserts that directly).
   *
   * **Its presence changes one thing in the dispatch above**: who the draft's verdict belongs to.
   * Absent — the tournament path — it is the *server's*, the value already on
   * `DocumentSnapshot.valid` that `GET /colony`, `POST /document` and `activate` report, carried
   * forward exactly as before. Present — the practice path — the server compiles nothing at all
   * (`rounds-local-run`: the compile gate is Run, on-device), so the draft is recorded with **no**
   * verdict rather than the server-derived one it used to inherit, and the device's answer follows.
   */
  onDraftCompile?: (source: string) => void;
  /**
   * **The rounds first-run guide** (WAG-11, design D-Guide's rounds counterpart). Inert-seam
   * convention, exactly like {@link phase}/{@link practice}: `false` (the default, and every
   * existing mount/test) draws no overlay at all — the composition root sets it `true` once it
   * is showing a real sandbox, never a bare/test mount. When enabled this screen reads/writes
   * the guide's step and durable "seen" mirror itself (mirroring `Colony.tsx`'s own wiring), so
   * it needs no further prop from the host beyond this flag.
   */
  firstRunGuideEnabled?: boolean;
  /** Injectable once-per-browser "seen" adapter for the rounds guide (design D-Persist),
   * keyed separately from the live Colony guide (`ROUNDS_ONBOARDING_SEEN_KEY`) so dismissing
   * one never suppresses the other. Defaults to a `window.localStorage`-backed adapter; tests
   * inject an in-memory double, exactly as `Colony.tsx`'s `onboardingSeenStore` does. */
  onboardingSeenStore?: OnboardingSeenStore;
}

const ROOT_STYLE: CSSProperties = {
  position: 'relative',
  width: '100%',
  overflow: 'hidden',
  background: 'var(--season-summer)',
};

/**
 * The field remains full-bleed, but the controls are a working area rather than page chrome. At
 * desktop widths an uncapped transport makes the playhead and its related controls feel unrelated,
 * while an uncapped prompt becomes an unusually long line of text. Below this width these styles
 * resolve to the exact existing mobile geometry (`width: 100%`).
 */
const SANDBOX_CONTROL_CONTENT_MAX_WIDTH = PROMPT_BAR_MAX_WIDTH;

/** A readable desktop measure for transient cards; mobile keeps its existing 12/16 px gutters. */
const SANDBOX_TRANSIENT_CARD_MAX_WIDTH = 640;

const COUNTER_LABELS = {
  bees: 'Living bees at the playhead',
  nectar: 'Stored nectar at the playhead',
  /** The same reading once a ceiling is derivable (WAG-100) — the pill then holds two numbers,
   * and a label naming only the first would leave the second unexplained. */
  nectarOfCapacity: 'Stored nectar at the playhead, of the hive’s capacity',
  score: 'This run’s final score',
} as const;

interface MutationGate {
  closed: boolean;
  reason: string;
}

/**
 * The presentational half of D6. This does not record a seal or send a request: phase data and a
 * local zero can make mutations unavailable early, while a server refusal remains authority.
 *
 * The spectator arm is defence in depth. Shipped rounds composition gives spectators no sandbox
 * scope, so it never mounts this screen for them; retaining the guard makes a later mount safe.
 */
function mutationGateFor(snapshot: RoundsPhaseSnapshot): MutationGate {
  if (!snapshot.known) return { closed: false, reason: '' };
  if (snapshot.isSpectator) return { closed: true, reason: 'Spectator members cannot change this sandbox.' };
  if (snapshot.surface === 'gathering') {
    return { closed: true, reason: 'Waiting for the presenter to start the round.' };
  }
  if (snapshot.locallyExpired) {
    return {
      closed: true,
      reason: 'Editing time has ended; waiting for the tournament to confirm the round.',
    };
  }
  if (!snapshot.mutationAllowed) return { closed: true, reason: 'Sandbox is closed for this round.' };
  return { closed: false, reason: '' };
}

export function SandboxScreen({
  coordinator,
  types = [],
  run = null,
  previousScore = null,
  chat,
  versionSelector,
  selectionCandidates = [],
  markSelection,
  onOpenStandings,
  reviewResult = null,
  onWatchGradedRound,
  dismissedDefaultingRounds,
  onDismissDefaultingRound,
  createRenderer,
  buildCandidate,
  speed: speedProp,
  onSpeed,
  admissibleSpeeds,
  onTogglePlayback,
  onSeek,
  onCreateType,
  frameHeight,
  frameWidth = REFERENCE_FRAME_WIDTH,
  phase,
  practice = null,
  promptLock = null,
  onDraftCompile,
  firstRunGuideEnabled = false,
  onboardingSeenStore = WINDOW_ROUNDS_ONBOARDING_SEEN_STORE,
}: SandboxScreenProps) {
  const state = useSyncExternalStore(
    useCallback((listener: () => void) => coordinator.subscribe(listener), [coordinator]),
    () => coordinator.getState(),
    () => coordinator.getState(),
  );
  const chatSnapshot = useChatSeam(chat);
  // The unknown snapshot (`known: false`) is what a bare mount or live mode gets with no `phase`
  // seam injected — see `useRoundsPhase`'s own contract. Every phase-chrome read below branches on
  // its fields rather than on whether `phase` itself was supplied, so there is exactly one "no
  // phase known" code path rather than two that could disagree.
  const phaseSnapshot = useRoundsPhase(phase);

  // --- the rounds first-run guide (WAG-11) -------------------------------------------------
  //
  // Reuses the store's `hasSeenColonyIntro`/`guideStep` slice — the same fields `Colony.tsx`
  // reads — rather than adding a second slice. This is safe, not a naming accident: `GameMode`
  // is resolved once at boot (`app/bootstrap.tsx`) and only one of `Colony`/`SandboxScreen` ever
  // mounts in a given browser tab (`docs/DESIGN.md` §2's "the two paths sit beside each other"),
  // so the two screens never contend for the field. Each screen re-seeds it from its OWN keyed
  // `localStorage` record on mount, so what the field means at any moment is simply "has THIS
  // mode's guide been acknowledged" — and because the guide is gated behind `firstRunGuideEnabled`
  // (default `false`, matching every existing/bare mount), no test that doesn't opt in is touched.
  const hasSeenGuideIntro = useClientStore((s) => s.hasSeenColonyIntro);
  const guideStep = useClientStore((s) => s.guideStep);
  // **Two ways in, one guide** (WAG-109). The first-run arrival is `!hasSeenGuideIntro`; the help
  // control is `guideRequested`. The second cannot be expressed as the first — the mirror below is
  // re-seeded from `localStorage` on every mount, so flipping it would close the guide again within
  // a frame, and it would misreport acknowledgement while it held.
  const guideRequested = useClientStore((s) => s.guideRequested);
  const showRoundsGuide = firstRunGuideEnabled && (!hasSeenGuideIntro || guideRequested);

  // Re-seed the durable mirror from THIS screen's own keyed record on mount (mirrors `Colony.tsx`'s
  // `useLayoutEffect` re-seed) — before paint, so a returning (already-seen) player never flashes
  // the guide for one frame. Skipped entirely while the guide is disabled: a bare/test mount that
  // never sets `firstRunGuideEnabled` touches neither the store nor `localStorage`.
  useLayoutEffect(() => {
    if (!firstRunGuideEnabled) return;
    useClientStore.getState().setHasSeenColonyIntro(onboardingSeenStore.hasSeenGuide());
  }, [firstRunGuideEnabled, onboardingSeenStore]);

  // Dismiss ("Got it"/"Skip"): persist the acknowledgement under the rounds-specific key and flip
  // the mirror true so the gate closes — the only exit that writes storage.
  //
  // `resetGuideProgress()` clears the help-control arm as well as the step, which is what makes the
  // control repeatable: without it `guideRequested` would stay true and the guide would re-open on
  // the next render, leaving no way to close it (WAG-109).
  function dismissRoundsGuide(): void {
    onboardingSeenStore.markGuideSeen();
    useClientStore.getState().setHasSeenColonyIntro(true);
    useClientStore.getState().resetGuideProgress();
  }

  // --- local, synchronous UI state -----------------------------------------------------
  const [draft, setDraft] = useState('');
  const [promptFocused, setPromptFocused] = useState(false);
  const [scopeName, setScopeName] = useState<string | null>(null);
  const [openTypeName, setOpenTypeName] = useState<string | null>(null);
  // The speed step is held here only when the host does not hold it. Whoever runs the frame loop
  // needs the step, so `RoundsController` owns it in production; a bare mount keeps a working
  // ladder rather than three buttons that never highlight.
  const [localSpeed, setLocalSpeed] = useState<SandboxSpeed>(DEFAULT_SANDBOX_SPEED);
  const speed = speedProp ?? localSpeed;
  const [scoreInfoOpen, setScoreInfoOpen] = useState(false);
  const [selectionCandidate, setSelectionCandidate] = useState<SandboxSelectionCandidate | null>(null);
  const [selectionPending, setSelectionPending] = useState(false);
  const [selectionMessage, setSelectionMessage] = useState<string | null>(null);

  /**
   * The height transition the world view, the stack and the prompt frame share when the input grows
   * on focus and settles on blur.
   *
   * `undefined` under `prefers-reduced-motion`, in which case the heights still change — they just
   * change at once, which is exactly what shipped before, so nothing is lost but the interpolation.
   *
   * Where `matchMedia` is absent (jsdom) the transition is *applied*, not skipped: an environment
   * that cannot report a motion preference has not expressed one, and jsdom does not animate
   * anyway. That is the same precedence `PlaybackBar`'s spinner and `ChatReentryRow` already use —
   * deliberately matched, so all three answer "is motion wanted?" the same way.
   */
  const growthTransition: string | undefined =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? undefined
      : PROMPT_GROWTH_TRANSITION;

  // --- the frame the whole layout is a remainder of --------------------------------------
  //
  // `sandbox-layout.ts` has always computed `worldHeight` as `frameHeight - stackHeight`, and its
  // own header calls the 844 px reference "a fallback, never an assumption, so a taller phone gives
  // its extra pixels to the world view rather than to a gap". Nothing ever handed it a real frame
  // height, though — `RoundsController` passes none — so every host got the reference, and on a
  // desktop window the screen drew 844 px tall with the rest of the viewport left blank below the
  // Beekeeper input.
  //
  // So the frame is **measured** when the host does not declare one. The arithmetic is untouched,
  // which is the point: the module's reason for existing (jsdom measures nothing, so a layout
  // expressed as `flex: 1` is one no test can hold to account) survives, and only the *input*
  // becomes real. An explicit `frameHeight` still wins, so the reference frame stays reproducible.
  //
  // No feedback loop: the root sizes itself from its host (`height: 100%`) in the measured case, so
  // measuring it cannot be perturbed by what the measurement then computes.
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [measuredFrameHeight, setMeasuredFrameHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (frameHeight !== undefined) {
      return undefined;
    }
    const el = rootRef.current;
    const apply = () => {
      const size = measuredSize(el);
      setMeasuredFrameHeight((current) =>
        size === null || current === size.height ? current : size.height,
      );
    };
    apply();
    if (el === null || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => observer.disconnect();
  }, [frameHeight]);

  // --- getting back into the conversation ------------------------------------------------
  //
  // The overlay is a popup, and a popup that has been closed is a conversation with no door.
  // `summarizeThread` derives the row's LINE from the same transcript the overlay draws, so the two
  // can never disagree on content; its own `busy` (== `transcript.running`) is not what the row
  // renders any more, though — `chatPhase` below is (`beekeeper-progress-indicator` §2), because a
  // send that has not yet reached `turnStarted` (`submitting`), or one whose outcome came back
  // ambiguous (`uncertain`), both need the row's busy/failure treatment and neither is `running`.
  // `view !== 'open'` covers BOTH ways the panel can be away (`player-chat-and-outcome` **D-2**):
  // dismissed outright, and minimised over a turn that is still running. The second is the one that
  // makes this row load-bearing rather than a convenience — while minimised it is the player's only
  // report of the conversation, so it keeps updating from the same live transcript the overlay
  // would be drawing.
  const threadSummary =
    chat !== undefined && chatSnapshot !== undefined && chatSnapshot.view !== 'open'
      ? summarizeThread(chatSnapshot.transcript)
      : null;
  const chatPhase: ChatPhase = chatSnapshot?.phase ?? 'idle';

  const layout: SandboxLayout = sandboxLayout({
    frameHeight: frameHeight ?? measuredFrameHeight ?? undefined,
    promptFocused,
    threadRowVisible: threadSummary !== null,
  });
  // --- derivations from the coordinator's state ----------------------------------------
  //
  // **Practice removes the tournament chrome rather than disabling it** (`client-sandbox-screen`,
  // `client-practice`). Every suppression below is unconditional on the framing's presence and does
  // not consult the phase snapshot, deliberately: a practice mount has no phase seam, so relying on
  // `known === false` would make the rule an accident of wiring rather than a property of the screen.
  // A caller that passed both a phase seam and a practice framing would still get no phase clock, no
  // round counter and no selection control, which is what makes the requirement falsifiable.
  const inPractice = practice !== null;
  const selectionAffordance: SelectionAffordance = inPractice
    ? { visible: false }
    : selectionAffordanceFor({
        submissionOpen: phaseSnapshot.submissionOpen,
        submission: phaseSnapshot.submission,
        memberKind: phaseSnapshot.memberKind,
      });
  const selectedCandidateLabel = selectionAffordance.visible
    ? selectionAffordance.submissionLabel
    : null;
  // A local zero is named as provisional presentation, never as a client-asserted seal. The
  // server remains authoritative when a mutating write returns its typed refusal.
  const mutationGate = mutationGateFor(phaseSnapshot);
  // **Withheld whenever a hand-edit save is already in flight** (adversarial review F1), not only
  // during a turn or a closed phase. `TypeSheet`'s own `save()` is optimistic — it returns to
  // read-only the instant Save is pressed, before the request settles — so without this the sheet
  // re-offered Edit while the first `saveDocument` was still on the wire. A second edit typed into
  // it would land in `state.draft` with no request of its own behind it (`hand-edit-requested`
  // already refuses a second save while `work` is busy), leaving it to be silently discarded the
  // moment the first request settled. `state.work.kind !== 'idle'` covers a submit too, matching
  // `transportInputFor`'s own `runInFlight` reading of the same field.
  const handEditWithheld = state.turnInFlight || mutationGate.closed || state.work.kind !== 'idle';
  const hud = sandboxHudLayout({ submittedBannerVisible: selectedCandidateLabel !== null });
  // **Whichever arm holds a trace**, through the coordinator's own unifier rather than a second
  // reading of the union here — `watchableTrace` is what `usePlayheadLoop` and the reducer's playhead
  // arms also ask, so the screen cannot end up drawing a run the playhead is not advancing through.
  // A watched run's trace is an unverified prefix; the *arm* is what records that (see `SandboxWorld`),
  // and from here on the world view's job is identical either way, which is the point of the shipped
  // `ReviewTrace` seam being the thing that crosses.
  const trace = watchableTrace(state);
  // **trace-required**, with one **fallback-permitted** carve-out (client-tick-rate-provenance,
  // tick-rate-single-source E4): no build default in general — before any trace has opened there
  // is no rate to state at all, so `0` is an explicit "unknown" sentinel, never a plausible rate
  // (the validated floor is 2 Hz). The one exception is a `runExecution: 'device'` run's progress
  // readout (`practiceProgress` below), which reports a position against a horizon BEFORE any
  // trace exists to watch — there the device-side practice path's own carve-out applies: the run
  // really is executing in `waggle-wasm`, compiled from the same `Balance::seed()` the mirror is
  // generated from, so `SIM_TICK_RATE_HZ` provably IS the running rate rather than a guess.
  const tickRateHz = trace?.tickRateHz ?? (state.runExecution === 'device' ? SIM_TICK_RATE_HZ : 0);
  const playheadTick = state.playback.playheadTick ?? trace?.firstTick ?? null;
  // **The counters' record, and deliberately the PLAYHEAD's own.** `client-sandbox-screen` is
  // normative that "an unavailable value renders a placeholder rather than a stale one": a readout
  // with no resident record shows its placeholder and retains nothing. The detail HOLD below moves
  // the drawn world frame, never these — a held frame is a picture a beat old, which is honest,
  // whereas a held *number* is a claim about the playhead that is simply false.
  const record = trace !== null && playheadTick !== null ? trace.detailAt(playheadTick) : null;
  const population = playheadPopulation(record, SANDBOX_SUBJECT_COLONY);
  const storedNectar =
    record?.colonies.find((colony) => colony.colony === SANDBOX_SUBJECT_COLONY)?.pool ?? null;
  // The pool's **ceiling** at the same playhead and off the same record (WAG-100): `live cells ×
  // per-cell capacity`. Derived rather than read, because no trace writes it down — see
  // `pool-capacity.ts`. `null` where the run declares no per-cell capacity, and the counter then
  // reads exactly as it did before, stating the stores and claiming no ceiling.
  const nectarCapacity = playheadPoolCapacity(
    record,
    SANDBOX_SUBJECT_COLONY,
    trace?.perCellCapacity,
  );

  // --- the world frame: which tick is DRAWN ---------------------------------------------
  //
  // **Hold the last detail frame across a short gap**, rather than dropping to coarse for the one
  // or two frames a lost prefetch race costs. Detail arrives one ~1-sim-second window at a time,
  // so without this the field strobes once a second at 1x: the bees vanish and the wide tier's
  // orange swarm markers flash up in their place. The prefetch that removes nearly every gap lives
  // in `RoundsController` (design **T1** — the screen issues no requests); this removes the flicker
  // from the ones it cannot (a cold seek, a slow response, a 16x sweep outrunning the network).
  //
  // What is shown during the hold is a **real detail frame, just a beat old** — the video player's
  // rule. The frame reports its own tick, so nothing claims the stale frame is the current one.
  const detailResident = record !== null;
  const lastResidentTickRef = useRef<number | null>(null);
  // Both arms carry a `runId`, and the held frame is invalidated by a change in **either** — including
  // the switch from a run's watched prefix to its published trace, which is a different (verified)
  // reading of the same run and must not inherit a frame decoded from the other.
  const runKey =
    state.world.kind === 'loaded'
      ? state.world.runId
      : state.world.kind === 'in-flight'
        ? -state.world.runId - 1
        : // A load that carried its own watched prefix through is still showing THAT prefix, so it is
          // the same reading of the same run and the held frame stays valid. Keying it as the
          // `in-flight` arm (rather than `null`) is what stops the hold being disarmed twice — once
          // entering the gap and once leaving it — for a picture that never changed.
          state.world.kind === 'loading' && state.world.watching !== null
          ? -state.world.runId - 1
          : null;
  // **The camera's own key, and deliberately not `runKey`.** The two ask different questions of the
  // same transition. A held detail *frame* must not survive the switch from a run's watched prefix to
  // its published trace — it was decoded from the other reading, so `runKey` flips sign on purpose.
  // The *camera* must survive it: publishing is the same run re-read, and reframing there threw away
  // the player's shot mid-run (measured: 4.00x -> 1.31x). So the camera keys on the run's bare
  // identity, which is arm-agnostic, and still changes when the run does.
  const cameraRunKey =
    state.world.kind === 'loaded' ||
    state.world.kind === 'in-flight' ||
    state.world.kind === 'loading'
      ? state.world.runId
      : null;

  // A run switch invalidates the previous run's held frame outright — a frame from run 7 is not a
  // stale view of run 8, it is a different world.
  //
  // Cleared **during render, not in an effect**: effects run in declaration order after paint, so
  // an effect here would fire on mount *after* the one below had recorded the opening tick and
  // would wipe it, leaving the hold permanently disarmed. Comparing against the previous key makes
  // the clear happen exactly on a change, and before `frameTickFor` reads the ref below.
  const lastRunKeyRef = useRef<number | null | undefined>(undefined);
  if (lastRunKeyRef.current !== runKey) {
    lastRunKeyRef.current = runKey;
    lastResidentTickRef.current = null;
  }

  useEffect(() => {
    if (detailResident && playheadTick !== null) {
      lastResidentTickRef.current = playheadTick;
    }
  }, [detailResident, playheadTick]);

  const frameTick =
    playheadTick === null
      ? null
      : frameTickFor({
          playheadTick,
          resident: detailResident,
          heldTick: lastResidentTickRef.current,
          holdTicks: detailHoldTicks(tickRateHz),
        });

  // --- the world camera -----------------------------------------------------------------
  //
  // A controlled `{centre, zoom}` (the Match/Colony convention) — the screen's own state, never
  // shared and never host-driven (UI.md §6: "position, zoom and play state are private to the
  // viewer"). It opens FRAMED ON the colony: a sandbox run is one colony whose hive sits on the
  // seeded spawn ring near the field edge, so a whole-field framing renders the run's entire
  // subject as a speck in a corner.
  //
  // **Framing is two halves, and the zoom used to be missing its.** The centre was always the
  // colony's hive-cell centroid; the zoom was pinned at `COLONY_CLOSE_SCALE`, a display constant
  // calibrated for the live Colony screen that measures nothing about the colony it frames. At
  // 1 screen pixel per world unit a 390 px frame shows 390 world units for a starting hive whose
  // extent is ~50 u — the colony under 2% of the frame's area, its bees below the size the art is
  // authored for. Both halves are now derived from the same cells (`colonyFitZoom`).
  //
  // **It is state, not a derivation.** The previous shape rebuilt the camera from
  // `hiveCentreOf(trace, playheadTick)` on every render, which is why panning and zooming did
  // nothing: whatever the player did was discarded on the next tick the playhead reached.
  const worldRef = useRef<HTMLDivElement | null>(null);
  // The world container's own measured size, or `null` before layout has produced one. Held as
  // the measurement itself rather than as a pre-seeded viewport, because the opening ZOOM — unlike
  // the opening centre — is a function of container size, so "we have not measured yet" has to be
  // a distinguishable state rather than a plausible-looking fallback the framing could run from.
  const [measured, setMeasured] = useState<{ width: number; height: number } | null>(null);
  // What the camera and the frame are actually drawn against: the measurement once there is one,
  // and the declared frame before that, so a camera derived pre-layout is centred rather than NaN.
  const viewport = useMemo(
    () => measured ?? { width: frameWidth, height: layout.worldHeight },
    [measured, frameWidth, layout.worldHeight],
  );

  // Measure the world container. The camera's offset is a function of container size
  // (`cameraForZoomCentre`), so deriving it from the reference-frame props instead put the colony
  // (measured − 390) / 2 px off centre at any real window size.
  useLayoutEffect(() => {
    const el = worldRef.current;
    const apply = () => {
      const size = measuredSize(el);
      if (size !== null) {
        setMeasured((current) =>
          current !== null && current.width === size.width && current.height === size.height
            ? current
            : size,
        );
      }
    };
    apply();
    if (el === null || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => observer.disconnect();
  }, [trace === null]);

  // The colony's home: the hive-cell centroid at the trace's FIRST tick — a colony's cells are the
  // only fixed marker of where it lives. Recentre whenever a trace opens; never afterwards, so a
  // player's camera survives playback.
  const homeCentre: [number, number] =
    trace === null ? [0, 0] : (hiveCentreOf(trace, trace.firstTick, SANDBOX_SUBJECT_COLONY) ?? [0, 0]);
  // The opening ZOOM, derived from the colony rather than declared: the shot at which the subject
  // colony's own hive cells, padded, fit the MEASURED container. `COLONY_CLOSE_SCALE` is what is
  // left when there is no colony to fit (no detail yet, no cells, or a degenerate extent) — it
  // keeps its live-mode meaning as the Colony screen's calibrated display constant and becomes
  // this screen's fallback rather than its default.
  const homeZoom = useMemo(() => {
    if (trace === null || measured === null) {
      return COLONY_CLOSE_SCALE;
    }
    return (
      colonyFitZoom(
        trace,
        trace.firstTick,
        SANDBOX_SUBJECT_COLONY,
        measured,
        SANDBOX_ZOOM_BOUNDS,
      ) ?? COLONY_CLOSE_SCALE
    );
    // The anchor is a dependency as well as the trace object, because a trace's IDENTITY does not
    // change when its first window becomes resident — the same `ReviewTrace` starts answering
    // `detailAt` with a record it previously answered `null` for. Memoizing on the trace alone
    // would therefore pin the fallback zoom taken before any detail existed and never recompute,
    // which is exactly the late-anchor case the framing has to follow.
    //
    // It deliberately does NOT name `trace.extentVersion`, even though a watched run's extent growing
    // is one way the anchor becomes known. The anchor itself is the honest trigger and it is already
    // here: `homeCentre` is recomputed every render, so it changes on the render where the cells land.
    // Naming the version would instead re-run `colonyFitZoom` on every one of a run's ~30 deltas to
    // notice the one that mattered — the per-delta churn `practice-watch.ts` exists to have removed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trace, measured, homeCentre[0], homeCentre[1]]);

  // The camera itself. Seeded from the same derived pair the framing effect uses, so there is one
  // definition of "the opening shot" rather than a constant here and a derivation below — on the
  // very first render nothing has been measured yet, so this is the `COLONY_CLOSE_SCALE` fallback
  // by construction, and the effect below replaces it the moment there is a container to fit.
  const [cameraView, setCameraView] = useState<{ centre: [number, number]; zoom: number }>(() => ({
    centre: homeCentre,
    zoom: homeZoom,
  }));

  // Frame on the colony when a run opens, and again when its anchor first becomes known — detail
  // arrives one window at a time, so at open `hiveCentreOf` commonly answers `null` and the anchor
  // is the field centre. That trigger is UNCONDITIONAL on purpose: a player who nudged the camera
  // during the pre-detail window must still receive their colony's framing when its cells land,
  // because a framing the screen never got to apply is not one the player chose to leave.
  //
  // The key carries whether a real measurement exists, so the first framing runs against the real
  // container rather than the pre-layout `REFERENCE_FRAME_WIDTH` seed — a fit computed from that
  // fallback would be wrong on any real window in a way a centre never was. It deliberately does
  // NOT carry the measured size itself: a later resize must leave the camera alone (today's
  // behaviour — `cameraForZoomCentre` recomputes the offset from the preserved centre and zoom, so
  // a resize is already correct), and reframing on one would be new functionality, not a fix.
  // The playhead stays out of the key entirely: "advancing the playhead SHALL NOT reframe the
  // view" is the regression this screen already fixed once. It keys on `cameraRunKey` and NOT on
  // `runKey` for the reason stated where the two are defined — publishing a watched run must not
  // reframe, while moving to a different run still must.
  const homeKey = `${cameraRunKey}:${measured === null ? 'unmeasured' : 'measured'}:${homeCentre[0].toFixed(1)}:${homeCentre[1].toFixed(1)}`;
  const lastHomeRef = useRef<string | null>(null);
  // The framing this screen ACTUALLY applied, snapshotted at the instant it applied it — which is
  // what "the shot the run opened at" means, and what Reset restores. It is not the same thing as
  // today's `homeZoom`: that value is a function of `measured`, so it moves on every resize, while
  // `homeKey` deliberately excludes the measured size so a resize does NOT reframe. Reading the
  // live value from `resetCamera` therefore restored a shot the run had never been shown at — a
  // run opened at 3.08 in a phone-width container jumped to the 4x clamp on Reset once the window
  // grew. Snapshotting closes that disagreement without making a resize reframe.
  const appliedHomeRef = useRef<{ centre: [number, number]; zoom: number } | null>(null);
  useEffect(() => {
    if (trace === null || measured === null || lastHomeRef.current === homeKey) {
      return;
    }
    lastHomeRef.current = homeKey;
    appliedHomeRef.current = { centre: homeCentre, zoom: homeZoom };
    setCameraView({ centre: homeCentre, zoom: homeZoom });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trace, homeKey]);

  const camera = cameraForZoomCentre(cameraView.centre, cameraView.zoom, viewport);

  function zoomBy(factor: number): void {
    setCameraView((current) => ({
      centre: current.centre,
      zoom: Math.min(SANDBOX_ZOOM_MAX, Math.max(SANDBOX_ZOOM_MIN, current.zoom * factor)),
    }));
  }

  function resetCamera(): void {
    // "Recentre" means "put the shot back the way it opened" — both the colony centre and the
    // derived opening zoom, never a fixed scale the run never opened at. The snapshot is the
    // authority; the live pair is the fallback for the window before any framing has been applied.
    setCameraView(appliedHomeRef.current ?? { centre: homeCentre, zoom: homeZoom });
  }

  // --- the picked bee (WAG-36) ------------------------------------------------------------
  //
  // "Picked" rather than "selected" throughout: `client-rounds-selection` is an existing, unrelated
  // capability about selecting a candidate *program set*, and this screen carries both affordances
  // (design **E9**). A pick is UI state and nothing else — it survives seek and scrub, and it is
  // the label layer, not this screen, that clears it when its bee leaves the trace.
  const [pickedBeeId, setPickedBeeId] = useState<number | null>(null);
  /** True while the playback track is being dragged: crowd labels are suppressed for its
   * duration. A picked bee's label is deliberately exempt. */
  const [scrubbing, setScrubbing] = useState(false);
  /** Bumped on every discontinuous playhead jump, so the label layer re-baselines silently
   * instead of firing a mass flash of the transitions the jump skipped over. */
  const [seekNonce, setSeekNonce] = useState(0);
  // A new run is a new world: the bee that was picked does not exist in it, whatever id it had.
  useEffect(() => {
    setPickedBeeId(null);
  }, [runKey]);

  // Gesture handlers: a STABLE object whose methods are reassigned every render (the Match screen's
  // convention), so the hook's native listeners never tear down and reattach on a camera-driven
  // re-render — which would lose an in-flight drag's captured pointer exactly when it matters.
  const gestureHandlersRef = useRef<CanvasCameraGestureHandlers>({
    onZoom: () => {},
    onPan: () => {},
  });
  // **Both handlers update from the PREVIOUS state, never from the render closure's `cameraView`.**
  //
  // They used to read `cameraView` directly, which silently loses gesture deltas whenever two
  // flushes land between two React commits: both compute from the same already-stale camera and the
  // second overwrites the first. That is rare when the screen is idle and constant while a run is in
  // flight, where progress reports were re-rendering the screen continuously — a pan was measured as
  // simply not working during a simulation while working perfectly in replay.
  //
  // It read as a *pan* bug specifically because of how the two gestures differ. A drag delivers many
  // small deltas, so almost all of them were clobbered; a wheel gesture accumulates its whole factor
  // inside the hook and flushes once per frame, and the rail's `zoomBy` was already written in this
  // form — so zoom appeared to work throughout. The updater form makes each delta compose on the last
  // regardless of when React commits.
  gestureHandlersRef.current.onZoom = (screenPoint, factor) => {
    const rect = worldRef.current?.getBoundingClientRect();
    if (rect === undefined || rect.width <= 0 || rect.height <= 0) {
      zoomBy(factor);
      return;
    }
    const local = { x: screenPoint.x - rect.left, y: screenPoint.y - rect.top };
    const size = { width: rect.width, height: rect.height };
    setCameraView((current) => {
      const from = cameraForZoomCentre(current.centre, current.zoom, size);
      const next = zoomAboutScreenPoint(from, local, factor, SANDBOX_ZOOM_BOUNDS);
      return {
        centre: screenToWorld(next, { x: size.width / 2, y: size.height / 2 }),
        zoom: next.scale,
      };
    });
  };
  gestureHandlersRef.current.onPan = (dxPx, dyPx) => {
    const rect = worldRef.current?.getBoundingClientRect();
    if (rect === undefined || rect.width <= 0 || rect.height <= 0) {
      return;
    }
    const size = { width: rect.width, height: rect.height };
    setCameraView((current) => {
      const panned = panByScreenDelta(
        cameraForZoomCentre(current.centre, current.zoom, size),
        dxPx,
        dyPx,
      );
      return {
        centre: screenToWorld(panned, { x: size.width / 2, y: size.height / 2 }),
        zoom: current.zoom,
      };
    });
  };
  // Tap to pick a bee, tap it again to clear (WAG-36). The geometry is `canvas/pick.ts`'s,
  // unchanged — the inverse-tilt screen→world hit test with its 44 px screen-space target, already
  // written and tested for the live Colony screen and reused here rather than restated.
  gestureHandlersRef.current.onTap = (screenPoint) => {
    const rect = worldRef.current?.getBoundingClientRect();
    if (rect === undefined || rect.width <= 0 || rect.height <= 0 || trace === null || frameTick === null) {
      return; // Unmeasured container: no jsdom safety net for tap pixel geometry (D-086).
    }
    const world = reviewWorldSnapshot(trace, frameTick);
    if (world === null) {
      return;
    }
    const local = { x: screenPoint.x - rect.left, y: screenPoint.y - rect.top };
    const size = { width: rect.width, height: rect.height };
    const picked = pickEntityAt(
      local,
      cameraForZoomCentre(cameraView.centre, cameraView.zoom, size),
      world.bees.values(),
    );
    // A tap that hits nothing LEAVES the pick alone, the Colony screen's rule: the field is a busy
    // moving surface, so a miss is far likelier than a deliberate clear — which is what the
    // explicit affordance is for.
    if (picked === null) {
      return;
    }
    setPickedBeeId((current) => (current === picked ? null : picked));
  };
  useCanvasCameraGestures(worldRef, gestureHandlersRef.current, true);

  const transport = transportStateFor(transportInputFor(state));
  const gatedTransport =
    mutationGate.closed && transport.activation === 'submit'
      ? {
          ...transport,
          activation: 'inert' as const,
          ariaDisabled: true,
          disabledAttribute: true,
          label: mutationGate.reason,
        }
      : transport;
  const extent = writtenExtentFor(state);
  /**
   * **A run in flight, as a position against its known horizon** (`client-practice` "Run progress is
   * determinate and the trace is watchable while it is written"; UI.md §4B, whose practice framing
   * names "the progress readout" as its own).
   *
   * Derived from the same {@link writtenExtentFor} the scrub track reads, so the readout and the
   * track can never tell two stories about how far the run has got — the drift the whole
   * three-layer track exists to avoid.
   *
   * `null` rather than a zeroed row where there is nothing to report: a readout pinned at `0:00`
   * reads as a stalled run, which is the fabricated value this screen's honesty rule forbids. And
   * `null` where the span is unknown, because that is the one case a determinate statement cannot
   * be made honestly — a local run always knows its horizon, so it is not a case practice reaches.
   */
  const practiceProgress: PracticeProgressView | null =
    extent.kind === 'in-flight' && extent.expectedTerminalTick !== null
      ? {
          ticksObserved: extent.ticksObserved,
          terminalTick: extent.expectedTerminalTick,
          label: `Simulating — ${clockLabel(extent.ticksObserved, tickRateHz)} of ${clockLabel(
            extent.expectedTerminalTick,
            tickRateHz,
          )}. Watch as it goes.`,
        }
      : null;
  const source = state.draft?.source ?? state.document?.source ?? '';
  const documentValid = state.draft?.compiles ?? state.document?.valid ?? true;
  const documentDiagnostics = state.draft?.diagnostics ?? state.document?.diagnostics ?? [];

  // The compiler's own words, **attributed to the slice they fall in**.
  //
  // The wire's `DocumentDiagnostic` carries only a line and a column, because the compiler reports
  // against the whole document — that is the unit it compiles. But **D12** requires the blocked-Run
  // popup to "offer to open the first offending location", and an open control needs a *where*. So
  // the line is attributed here, with `document-slice.ts`'s `typeAtLine` — the inverse of the same
  // header scan the Code tab already uses to show a slice.
  //
  // `null` stays a real answer: a diagnostic above the first `type` header belongs to the
  // document's preamble, and `PlaybackBar` offers no open control for one, rather than sending the
  // player to an invented location.
  const documentSource = state.draft?.source ?? state.document?.source ?? '';
  const scriptDiagnostics: ScriptDiagnostic[] = useMemo(
    () =>
      documentDiagnostics.map((diagnostic) => ({
        typeName: typeAtLine(documentSource, diagnostic.line),
        line: diagnostic.line,
        message: diagnostic.message,
      })),
    [documentDiagnostics, documentSource],
  );

  const openType = types.find((t) => t.name === openTypeName) ?? null;
  const openTypeIndex = openType === null ? 0 : types.indexOf(openType);

  // --- the phase chrome's two mountings ---------------------------------------------------
  //
  // Both popups are inset with a uniform margin on every side, so between them they cover the
  // whole world view including its corner (`client-sandbox-screen`'s composition requirement).
  // The corner pill is therefore hidden while either is open — it MAY be, and it is, because both
  // popups carry the bare remainder in their own header instead (`headerRemainder` below).
  const chatOpen = chat !== undefined && chatSnapshot?.view === 'open';
  const popupOpen = openType !== null || chatOpen;

  // --- the three waiting states -----------------------------------------------------------
  //
  // "Has the player produced a candidate this round" is read straight off the coordinator's own
  // state rather than a count this screen has nowhere to get (`ui/rounds/waiting-state.ts`'s
  // header explains why): once a run has ever landed or a trace has ever loaded, both flip and
  // never revert within this round's coordinator, regardless of which version is looked at next.
  // The preview trace does not count (`client-sandbox-screen`, "not counted among the player's
  // runs anywhere on the screen"): it is nobody's run, and treating its presence as a candidate
  // would both suppress the "nothing has been run yet" state it exists to stand in for and — via
  // `waiting-on-others` reading a submission that does not exist — never actually misfire, but
  // would still be the wrong reason for the right answer.
  const hasAnyCandidate =
    state.run.kind !== 'none' || (state.world.kind === 'loaded' && state.world.isPreview !== true);
  const previewActive = state.world.kind === 'loaded' && state.world.isPreview === true;

  // --- the shared preview trace (`rounds-preview-run`, `client-sandbox-screen`) -----------
  //
  // This screen sequences nothing (design **T1**/**T2**): it dispatches an *intent*, and the
  // coordinator's own `open-preview` effect and `preview-settled` handling own the request and
  // the retry rule. `preview-requested` is idempotent while an attempt is already in flight or
  // the world is already claimed, so re-dispatching it costs nothing beyond the read the
  // coordinator would have refused anyway. Practice has no tournament and asks for nothing.
  useEffect(() => {
    if (inPractice || hasAnyCandidate || state.world.kind !== 'empty') {
      return;
    }
    coordinator.dispatch({ kind: 'preview-requested' });
    // `phaseSnapshot` is the retry clock: the phase channel republishes only on an actual
    // change (a new `joined` count, a phase turnover), which is exactly the cadence a
    // "try again later" read should have — no poll of this screen's own.
  }, [inPractice, hasAnyCandidate, state.world.kind, phaseSnapshot, coordinator]);

  // A practice session has no phase, so it has no waiting state to name and no round result to
  // report. `{kind: 'none'}` rather than the snapshot's answer: a caller that supplied a phase seam
  // alongside a practice framing must still get no round chrome.
  //
  // The "nothing has been run yet" card is suppressed while the preview is showing — a working
  // hive on screen is the opposite of nothing, even though the preview itself does not count as
  // the player's own candidate (`hasAnyCandidate` stays strict for the effect gate above, so a
  // real run still supersedes the preview exactly as before).
  const waiting: WaitingState = inPractice
    ? { kind: 'none' }
    : waitingStateFor({ phase: phaseSnapshot, hasAnyCandidate: hasAnyCandidate || previewActive });

  // --- the G9 defaulting notice ------------------------------------------------------------
  //
  // Shown **once** per (round, player) — here, per round, since this screen is always the
  // caller's own sandbox and there is only ever one player it could be about. Dismissal is what
  // "once" means: the notice stays up (it is not a toast that vanishes on its own) until either
  // the player dismisses it or the round moves on, and a repeated poll or a reconnect reporting
  // the identical fact for a round already dismissed must not resurrect it — which is exactly
  // what keying by round number, rather than a single boolean, gives for free at a round boundary.
  const [localDismissedNoticeRounds, setLocalDismissedNoticeRounds] = useState<ReadonlySet<number>>(
    () => new Set(),
  );
  const dismissedNoticeRounds = dismissedDefaultingRounds ?? localDismissedNoticeRounds;
  const defaultingSubmission: SubmissionReadDto | null =
    !inPractice &&
    phaseSnapshot.known &&
    phaseSnapshot.surface !== 'editing' &&
    phaseSnapshot.round !== null &&
    !dismissedNoticeRounds.has(phaseSnapshot.round) &&
    phaseSnapshot.submission !== null &&
    isDefaultedProvenance(phaseSnapshot.submission.provenance)
      ? phaseSnapshot.submission
      : null;

  function dismissDefaultingNotice(): void {
    const round = phaseSnapshot.round;
    if (round === null) {
      return;
    }
    if (onDismissDefaultingRound !== undefined) {
      onDismissDefaultingRound(round);
      return;
    }
    setLocalDismissedNoticeRounds((current) => {
      const next = new Set(current);
      next.add(round);
      return next;
    });
  }

  // --- handlers: dispatch, never sequence ----------------------------------------------
  function activateTransport(activation: TransportActivation): void {
    if (activation === 'submit' && mutationGate.closed) {
      return;
    }
    if (activation === 'submit') {
      const candidate = buildCandidate?.(source) ?? null;
      if (candidate !== null) {
        coordinator.dispatch({ kind: 'run-requested', candidate });
      }
      return;
    }
    if (activation === 'toggle-playback') {
      // Play ⇄ pause, and — at the terminal tick — a restart from the first. Which of the three it
      // is belongs to the reducer, which holds the trace's own span; this is the press, said once.
      coordinator.dispatch({ kind: 'playback-toggled' });
      onTogglePlayback?.();
    }
    // `diagnostics` raises `PlaybackBar`'s own popup over the compiler's messages; `inert` is a
    // control that receives nothing. Neither is this file's decision.
  }

  /** A drag that `PlaybackBar` already resolved to a tick. Dispatched rather than applied: the
   * coordinator re-asks `written-extent.ts` whether the seek may land at all, so an in-flight run
   * refuses it there too and the track's affordance can never be the only thing enforcing it. */
  function seekTo(tick: number): void {
    coordinator.dispatch({ kind: 'seek', tick });
    // A discontinuous jump, so the label layer re-baselines on the landing frame (WAG-36).
    setSeekNonce((n) => n + 1);
    onSeek?.(tick);
  }

  function selectSpeed(step: SandboxSpeed): void {
    setLocalSpeed(step);
    onSpeed?.(step);
  }

  function sendPrompt(): void {
    const text = draft.trim();
    if (text === '' || chat === undefined || mutationGate.closed || promptLock !== null) {
      return;
    }
    // The scope travels **with** the message: the Beekeeper's unit of work is the whole colony
    // document, and the type is context for the request rather than a target it is confined to.
    chat.sendAndOpen(text, scopeName);
    setDraft('');
    setPromptFocused(false);
  }

  function selectType(name: string): void {
    setOpenTypeName(name);
    setScopeName(name);
  }

  /** A hand edit, as the two events the coordinator expects — whether it arrived as one type's
   * slice or as the degraded mode's whole document. */
  function commitDocument(next: string): void {
    coordinator.dispatch({
      kind: 'document-edited',
      source: next,
      // **Whose verdict this is** (task 5.12). With no `onDraftCompile` — the tournament path — it
      // is the server's, carried forward as before. With one — practice — the server compiles
      // nothing, so carrying `documentValid` here would attach a *server*-derived verdict to bytes
      // no server has seen. Omitted means "not known yet", and the host dispatches `draft-compiled`
      // when the device answers.
      ...(onDraftCompile === undefined ? { compiles: documentValid } : {}),
      diagnostics: documentDiagnostics,
    });
    coordinator.dispatch({ kind: 'hand-edit-requested' });
    onDraftCompile?.(next);
  }

  /** The confirmation was pressed. Every guard here is a synchronous read of state this screen
   * already holds; the request itself belongs to `requestSelection`, the module that owns the
   * promise — this screen sequences nothing (design **T1** and **T2**, asserted by
   * `SandboxScreen.host.test.tsx`). What comes back is one line of copy, so no outcome is
   * interpreted here and none is held across time. */
  function confirmSelection(): void {
    if (
      selectionCandidate === null ||
      !selectionAffordance.visible ||
      !selectionAffordance.enabled ||
      mutationGate.closed ||
      markSelection === undefined
    ) {
      return;
    }
    setSelectionPending(true);
    // The candidate identity is the complete request. In particular, this path deliberately
    // does not read `source`, per-type programs, or the birth type.
    //
    // A settled selection changes no local submission state either way: the next phase reading
    // remains the source of the banner, including when this request raced the submission boundary.
    requestSelection(markSelection, selectionCandidate.candidateId, (message) => {
      setSelectionMessage(message);
      setSelectionPending(false);
      setSelectionCandidate(null);
    });
  }

  // **No starter-prompt chip row on this screen** (2026-08-01, on review of the running screen).
  //
  // Design §3.5 retained it, floated over the world while the draft is empty. Built and looked at,
  // it reads as permanent chrome: the draft is empty almost all the time, so the row is almost
  // always up — and it sits over the run the player is watching, which is the one thing the world
  // view exists for. `screen-at-rest.html` draws the at-rest screen with no chips, so the sketch
  // was already the other reading.
  //
  // Nothing normative is given up: no requirement in `client-sandbox-screen` mentions starter
  // prompts and no `// covers:` marker binds them. `PromptBox` keeps its `starterPrompts` prop for
  // live mode, where the row still sits inside the sheet. Recorded as a deviation from §3.5 rather
  // than absorbed silently; re-introducing it is a prop away.

  // **UI/UX audit F-07.** The empty-world hint and the card stack are both centred over the world
  // area, so whenever a card occupies it the hint is a second, fainter copy of the same message
  // drawn underneath — which is exactly what the audit measured. One expression, listing the same
  // three cards the JSX below chooses between, so the two cannot disagree about whether that space
  // is taken.
  const worldHintSuppressed =
    defaultingSubmission !== null ||
    waiting.kind === 'reviewing' ||
    waitingCardCopy(waiting) !== null;

  /**
   * **The dead gap between "Run requested" and the first frame drawn.**
   *
   * `state.world.kind` stays `'empty'` — with whatever reason it already carried — for the whole
   * time a run is in flight but has not yet reported a view: a fresh practice session's wasm module
   * takes ~10 s to warm up before it steps at all, and a server-executed run has a shorter but real
   * gap between its accept and its first poll answer. Left alone, `emptyWorldCopy` kept showing
   * "press Run to simulate this one" — a stale instruction for a run the player already started.
   *
   * A run in flight therefore pre-empts the ordinary empty-world reasons outright, and reuses
   * `practiceProgress`'s own label once the run has one, rather than composing a second copy of the
   * same sentence — so this readout and the practice corner tag's can never disagree about the same
   * run. Before a horizon is known (the warmup itself), it falls back to the plain statement.
   */
  const worldEmptyCopy =
    state.run.kind === 'in-flight'
      ? (practiceProgress?.label ?? 'Simulating your colony…')
      : emptyWorldCopy(state);

  return (
    <div
      ref={rootRef}
      data-testid="sandbox-screen"
      // A declared frame is drawn at exactly that height (the reference frame, and every test that
      // pins one); otherwise the screen fills its host and the measurement above turns that into the
      // layout's frame. `100%` rather than `100dvh`: the app shell already establishes the
      // html/body/#root height chain (`app-shell.css`, D-085), so this resolves against the viewport
      // without this screen asserting it owns one.
      //
      // **The stack and world heights are published here** (WAG-91, `sandboxLayoutVars`) so anything
      // positioned against this screen's geometry reads the height actually being drawn rather than
      // a copy of the resting constants. The version selector's panel is the consumer: it arrives as
      // an opaque slot, so a custom property is the only seam that joins it to this arithmetic.
      style={{ ...ROOT_STYLE, height: frameHeight ?? '100%', ...sandboxLayoutVars(layout) }}
    >
      {/* ── The world view ──────────────────────────────────────────────────────────────── */}
      <div
        ref={worldRef}
        data-testid="sandbox-world"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: layout.worldHeight,
          // Anchored to the top edge while the stack is anchored to the bottom, so the two must
          // trade height on one clock or a gap opens at the seam mid-transition.
          transition: growthTransition,
          overflow: 'hidden',
          // Browser page-zoom/scroll suppression over the field is unconditional, the same rule the
          // Match and Colony canvases follow (UI.md §5).
          touchAction: 'none',
          cursor: 'grab',
        }}
      >
        {trace !== null && frameTick !== null ? (
          <ReviewFrame
            trace={trace}
            tick={frameTick}
            camera={camera}
            viewport={viewport}
            // The anchor the frame draws the hive tile and the own queen at: the DRAWN tick's comb
            // centroid where the range is resident, else the opening one. Not `homeCentre` alone —
            // the comb grows through a run as builders add cells, and pinning the tile to the
            // tick-0 centroid would slide it out from under its own cells.
            hive={hiveCentreOf(trace, frameTick, SANDBOX_SUBJECT_COLONY) ?? homeCentre}
            subjectColony={SANDBOX_SUBJECT_COLONY}
            // WAG-36. The subject gate is a no-op on this screen — the subject IS the player's
            // own colony — so what these three actually carry is the scrub suppression, the seek
            // re-baseline, and the pick.
            scrubbing={scrubbing}
            seekNonce={seekNonce}
            pickedBeeId={pickedBeeId}
            onPickedBeeChange={setPickedBeeId}
            createRenderer={createRenderer}
          />
        ) : (
          // UI/UX audit **F-07**, two defects in one element:
          //
          //   1. **It collided with the waiting card.** Both this layer and `WaitingCard` centre
          //      themselves over the same world area, so "Nothing has been run yet" and "Pick a
          //      version to watch its run…" were drawn on top of one another — saying nearly the
          //      same thing twice. The card is the better of the two (it is legible and states the
          //      action), so this layer now yields to it: `worldHintSuppressed` is true whenever a
          //      card of any kind occupies that space.
          //   2. **It was illegible.** `--text-muted` is an ink token meant for cream cards; on the
          //      world green it measured **1.61:1**. No text colour reads reliably against a live
          //      world, so the hint now carries its own `--hud-bg` pill rather than a darker ink.
          <div
            data-testid="sandbox-world-empty"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 24px',
              textAlign: 'center',
              // Never intercepts a camera gesture: this is a caption, not a control.
              pointerEvents: 'none',
            }}
          >
            {!worldHintSuppressed && worldEmptyCopy !== '' && (
              <span
                data-testid="sandbox-world-empty-hint"
                role="status"
                style={{
                  maxWidth: 420,
                  padding: '8px 14px',
                  borderRadius: 'var(--r-pill)',
                  background: 'var(--hud-bg)',
                  border: '1px solid var(--line-strong)',
                  boxShadow: 'var(--shadow-hud)',
                  fontFamily: 'var(--font-body)',
                  fontSize: 13,
                  color: 'var(--ink-2)',
                }}
              >
                {worldEmptyCopy}
              </span>
            )}
          </div>
        )}

        {/* Recentre-on-the-colony. Rendered only with a trace up: there is nothing to recentre on
            over an empty world. Zoom itself is gestural — pinch on touch, wheel on a pointer
            (WAG-93) — so no rail sits over the field to duplicate it. */}
        {trace !== null && <ReviewCameraControl zoom={cameraView.zoom} onReset={resetCamera} />}

        {/* The way out of a pick (WAG-36 task 9.4). Bottom-centre of the world view, clear of the
            counters, the phase clock and the zoom rail — an absolutely-positioned overlay, so the
            exactly-accounted height budget (UI.md §4B) is untouched. */}
        {pickedBeeId !== null && (
          <PickedBeeChip
            beeId={pickedBeeId}
            onClear={() => setPickedBeeId(null)}
            testId="sandbox-clear-pick"
            style={{ bottom: 12, left: '50%', transform: 'translateX(-50%)' }}
          />
        )}

        {/* The G5 phase clock's resting mounting: the corner opposite the counters. Hidden while
            either popup is open — both cover this corner, and each carries the remainder in its
            own header instead (`headerRemainder` below). `PhaseClock` itself renders nothing for
            the unknown snapshot, so a bare mount or live mode (no `phase` seam) draws nothing
            here regardless of `popupOpen`. */}
        {!popupOpen &&
          (practice !== null ? (
            // The vacated slot carries the session's own identifier, so the screen states what it
            // is rather than being defined by the absence of a clock.
            <PracticeTag
              framing={practice}
              progress={practiceProgress}
              style={{ position: 'absolute', top: hud.phaseClockTop, left: hud.phaseClockLeft }}
            />
          ) : (
            <PhaseClock
              snapshot={phaseSnapshot}
              variant="corner"
              style={{ position: 'absolute', top: hud.phaseClockTop, left: hud.phaseClockLeft }}
            />
          ))}

        {onOpenStandings !== undefined && !inPractice && (
          <button
            type="button"
            data-testid="sandbox-open-standings"
            aria-label="Open standings"
            onClick={onOpenStandings}
            style={{
              position: 'absolute',
              zIndex: 3,
              top: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              minWidth: 'var(--hit-target)',
              height: 'var(--hit-target)',
              padding: '0 12px',
              borderRadius: 'var(--r-pill)',
              border: '1px solid var(--hud-line)',
              background: 'var(--hud-bg)',
              color: 'var(--ink-1)',
              fontFamily: 'var(--font-display)',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Standings
          </button>
        )}

        <CounterStack
          population={population.total}
          nectar={storedNectar}
          nectarCapacity={nectarCapacity}
          run={run}
          previousScore={previousScore}
          infoOpen={scoreInfoOpen}
          onToggleInfo={() => setScoreInfoOpen((open) => !open)}
          top={hud.counterStackTop}
        />

        {selectedCandidateLabel !== null && (
          <SubmittedSetBanner
            label={selectedCandidateLabel}
            editorAhead={
              state.draft !== null ||
              state.selection.kind !== 'settled' ||
              phaseSnapshot.submission?.candidateRef !== `run:${state.selection.runId}`
            }
            top={hud.submittedBannerTop}
            height={hud.submittedBannerHeight}
          />
        )}

        {mutationGate.closed && (
          <div
            data-testid="sandbox-mutation-gate-reason"
            role="status"
            style={{
              position: 'absolute',
              left: 12,
              right: 12,
              bottom: 12,
              padding: '8px 10px',
              borderRadius: 'var(--r-md)',
              background: 'var(--paper-1)',
              border: '1px solid var(--line-strong)',
              fontFamily: 'var(--font-body)',
              fontSize: 12,
              color: 'var(--ink-2)',
            }}
          >
            {mutationGate.reason}
          </div>
        )}

        {/* The G9 defaulting notice takes priority over the ordinary waiting card: a round that
            just closed with a defaulted submission is "simulating" AND "the player was defaulted
            into it", and the notice is the more specific — and more important — of the two facts
            to surface (`prototypes.md`'s panel 4). */}
        {defaultingSubmission !== null ? (
          <DefaultingNoticeCard submission={defaultingSubmission} onDismiss={dismissDefaultingNotice} />
        ) : waiting.kind === 'reviewing' ? (
          <ReviewResultCard result={reviewResult} round={waiting.round} onWatch={onWatchGradedRound} />
        ) : (
          <WaitingCard state={waiting} />
        )}

        {/* The preview trace is presented honestly as what it is (`client-sandbox-screen`): the
            round's starting scripts, not a candidate and not one of the player's own runs. A
            small non-interactive label rather than a card, so it does not compete with the
            waiting-card stack above for the same space while the player has genuinely produced
            nothing of their own yet. */}
        {previewActive && (
          <div
            data-testid="sandbox-preview-banner"
            style={{
              position: 'absolute',
              top: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              padding: '4px 10px',
              borderRadius: 'var(--r-pill)',
              background: 'var(--hud-bg)',
              border: '1px solid var(--hud-line)',
              fontFamily: 'var(--font-body)',
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--ink-2)',
              pointerEvents: 'none',
            }}
          >
            Round’s starting scripts
          </div>
        )}

        {state.notice !== null && (
          <div
            data-testid="sandbox-notice"
            role="status"
            style={{
              position: 'absolute',
              left: 12,
              right: 12,
              bottom: 12,
              padding: '8px 10px',
              borderRadius: 'var(--r-md)',
              background: state.notice.tone === 'error' ? 'var(--danger-soft)' : 'var(--hud-bg)',
              border: `1px solid ${state.notice.tone === 'error' ? 'var(--danger)' : 'var(--hud-line)'}`,
              fontFamily: 'var(--font-body)',
              fontSize: 12,
              color: state.notice.tone === 'error' ? 'var(--danger)' : 'var(--ink-2)',
            }}
          >
            {state.notice.message}
            {/* **The compiler's own words, not just their count.**
                A `compileFailed` refusal already carries the diagnostics into the notice
                (`SandboxNotice.diagnostics`) and the message states how many there are — but until
                this list existed the sentences themselves were dropped on the floor, leaving the
                player with "2 errors" and nowhere to read them. That is the surface
                `rounds-local-run`'s on-device compile gate reports through: it refuses before any
                request, so the diagnostics in this list are the only account of *why*. Also the
                right treatment for the shipped server-side refusal, which had the same gap. */}
            {state.notice.diagnostics.length > 0 && (
              <ul
                data-testid="sandbox-notice-diagnostics"
                style={{
                  margin: '6px 0 0',
                  paddingInlineStart: 16,
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  lineHeight: 1.55,
                }}
              >
                {state.notice.diagnostics.map((diagnostic, index) => (
                  <li key={`${diagnostic.typeName}-${index}`}>
                    {diagnostic.typeName === ''
                      ? diagnostic.message
                      : `${diagnostic.typeName} — ${diagnostic.message}`}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* ── The control stack: exactly three bars ───────────────────────────────────────── */}
      <div
        data-testid="sandbox-stack"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: layout.stackHeight,
          // The other half of the trade — same clock as the world view above.
          transition: growthTransition,
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--hud-bg)',
          borderRadius: 'var(--r-lg) var(--r-lg) 0 0',
          boxShadow: 'var(--shadow-sheet)',
          // An inset rule rather than a border: a 1.5px border inside the box would come out of a
          // bar's content height, which is the discrepancy visual spec §0.4 quirk 1 records.
          backgroundImage: 'linear-gradient(var(--hud-line), var(--hud-line))',
          backgroundSize: '100% 1.5px',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div
          data-testid="sandbox-playback-bar"
          style={{
            width: '100%',
            maxWidth: SANDBOX_CONTROL_CONTENT_MAX_WIDTH,
            height: layout.playbackHeight,
            flex: 'none',
            margin: '0 auto',
          }}
        >
          <PlaybackBar
            transport={gatedTransport}
            extent={extent}
            playheadTick={playheadTick}
            tickRateHz={tickRateHz}
            speed={speed}
            admissibleSpeeds={admissibleSpeeds}
            onSpeed={selectSpeed}
            onActivate={activateTransport}
            onSeek={seekTo}
            onDragChange={setScrubbing}
            versionSelector={
              <div
                aria-disabled={mutationGate.closed || undefined}
                style={{ pointerEvents: mutationGate.closed ? 'none' : undefined }}
                // The selector is an opaque slot, so the gate has to protect both pointer and
                // keyboard activation at its boundary. `aria-disabled` alone would leave an
                // embedded button's Enter/Space handler live.
                onClickCapture={(event) => {
                  if (mutationGate.closed) {
                    event.preventDefault();
                    event.stopPropagation();
                  }
                }}
                onKeyDownCapture={(event) => {
                  if (mutationGate.closed) {
                    event.preventDefault();
                    event.stopPropagation();
                  }
                }}
              >
                {versionSelector}
              </div>
            }
            // The help control is offered only where the guide can actually be shown — with the
            // guide disabled (every bare/test mount) a `?` that opened nothing would be a dead
            // affordance rather than a harmless extra.
            onHelp={
              firstRunGuideEnabled ? () => useClientStore.getState().requestGuide() : undefined
            }
            diagnostics={scriptDiagnostics}
            // "Offer to open the first offending location" (**D12**). The popup only renders this
            // for a diagnostic that names a type, so a preamble error offers nothing rather than
            // opening an invented slice.
            onOpenDiagnostic={(diagnostic) => {
              if (diagnostic.typeName !== null) {
                selectType(diagnostic.typeName);
              }
            }}
          />
        </div>

        <div
          data-testid="sandbox-type-bar"
          // `boxSizing` explicitly: this app ships no global border-box reset, and a bar whose
          // padding was added to its height would put the stack 32 px past its budget.
          style={{
            width: '100%',
            maxWidth: SANDBOX_CONTROL_CONTENT_MAX_WIDTH,
            height: layout.typesHeight,
            boxSizing: 'border-box',
            flex: 'none',
            margin: '0 auto',
            padding: '8px 12px',
          }}
        >
          <BeeTypeBar
            types={types.map((type, index) => ({
              name: type.name,
              count: countForType(population, type.typeId),
              color: colorForType(type.name, index),
              sprite: spriteForType(type.name),
              abbrev: abbrevForType(type.name),
              alert: type.alert,
            }))}
            selectedName={openTypeName ?? undefined}
            onSelect={selectType}
            onAdd={mutationGate.closed ? undefined : onCreateType}
            style={{ height: '100%', background: 'transparent', border: 'none', boxShadow: 'none', padding: 0 }}
          />
        </div>

        <PromptBar
          height={layout.promptHeight}
          draft={draft}
          focused={promptFocused}
          scopeName={scopeName}
          growthTransition={growthTransition}
          canSend={chat !== undefined}
          // The composer is disabled for the whole of the mutation gate AND for the whole of
          // `isChatBusy` (`beekeeper-progress-indicator` §2) — a visible echo of the channel's own
          // one-send-at-a-time guard (`sendAndOpen` already refuses a second send silently; this
          // just makes the box look as inert as it already behaves), and, unlike the mutation gate,
          // `uncertain` is included: nothing was verifiably NOT admitted, so a resend here would be
          // exactly the duplicate-send risk the phase exists to prevent.
          disabled={mutationGate.closed || promptLock !== null || isChatBusy(chatPhase)}
          disabledReason={
            (promptLock?.reason ?? mutationGate.reason) ||
            (isChatBusy(chatPhase) ? 'The Beekeeper is already working on this.' : '')
          }
          lock={promptLock}
          threadSummary={threadSummary}
          chatBusy={chatPhase === 'submitting' || chatPhase === 'running'}
          chatFailed={chatPhase === 'failed'}
          chatUncertain={chatPhase === 'uncertain'}
          chatNoOutputSettle={chatSnapshot?.noOutputSettle}
          chatUnread={chatSnapshot?.unread === true}
          chatMinimised={chatSnapshot?.view === 'minimised'}
          onReopenThread={() => chat?.openExisting()}
          onDraft={setDraft}
          onFocus={() => setPromptFocused(true)}
          onBlur={() => setPromptFocused(false)}
          onClearScope={() => setScopeName(null)}
          onSend={sendPrompt}
        />
      </div>

      {selectionAffordance.visible && markSelection !== undefined && selectionCandidates.length > 0 && (
        <CandidateSelectionSurface
          candidates={selectionCandidates}
          affordance={selectionAffordance}
          bottom={layout.stackHeight + 12}
          disabled={mutationGate.closed}
          disabledReason={mutationGate.closed ? mutationGate.reason : selectionAffordance.reason}
          onChoose={(candidate) => {
            setSelectionMessage(null);
            setSelectionCandidate(candidate);
          }}
        />
      )}

      {selectionCandidate !== null && selectionAffordance.visible && (
        <SelectionConfirmation
          candidate={selectionCandidate}
          copy={selectionAffordance.confirmation}
          pending={selectionPending}
          onCancel={() => !selectionPending && setSelectionCandidate(null)}
          onConfirm={confirmSelection}
        />
      )}

      {selectionMessage !== null && (
        <div
          data-testid="sandbox-selection-message"
          role="status"
          style={{
            position: 'absolute',
            left: '50%',
            width: 'calc(100% - 24px)',
            maxWidth: SANDBOX_TRANSIENT_CARD_MAX_WIDTH,
            boxSizing: 'border-box',
            transform: 'translateX(-50%)',
            bottom: layout.stackHeight + 12,
            zIndex: 12,
            padding: '8px 10px',
            borderRadius: 'var(--r-md)',
            background: 'var(--paper-1)',
            border: '1px solid var(--line-strong)',
            fontFamily: 'var(--font-body)',
            fontSize: 12,
            color: 'var(--ink-2)',
          }}
        >
          {selectionMessage}
        </div>
      )}

      {/* ── The popups. Both float over a live control stack; neither has a scrim. ─────── */}
      {openType !== null && (
        <TypeSheet
          typeId={openType.typeId}
          typeName={openType.name}
          color={colorForType(openType.name, openTypeIndex)}
          sprite={spriteForType(openType.name)}
          buff={openType.buff ?? null}
          birthType={openType.birthType}
          colonyDocument={source}
          documentValid={documentValid}
          documentDiagnostics={documentDiagnostics}
          population={population}
          playheadLabel={playheadTick === null ? undefined : clockLabel(playheadTick, tickRateHz)}
          turnInFlight={handEditWithheld}
          // Two events, in order: the editor's text becomes the draft, then the draft is committed.
          // The screen states what happened; the coordinator decides what it means.
          onSaveDocument={mutationGate.closed ? undefined : commitDocument}
          // **The ordinary hand edit** (`client-sandbox-screen`: the code view "SHALL show the
          // type's verbatim slice of the colony document and SHALL be editable"). Until this was
          // supplied the sheet resolved its commit to `onSaveSlice` for every document that parses
          // and found nothing there, so the ✎ Edit control rendered *only* in the degraded repair
          // mode — a player with a working colony had no hand edit at all (E2E finding M-6). What
          // is saved is still the whole document: the slice goes back where `sliceType` took it
          // from, and the request downstream is unchanged.
          onSaveSlice={
            mutationGate.closed
              ? undefined
              : (next) => commitDocument(replaceTypeSlice(source, openType.name, next))
          }
          onClose={() => setOpenTypeName(null)}
          // The editing mounting: the bare remainder rides this sheet's own header while it is
          // the player's surface (`client-rounds-phase`: "the remainder stays on screen with the
          // bee-type sheet open"). `PhaseClock` renders nothing for the unknown snapshot, so a
          // bare mount or live mode's `Colony.tsx` (which never passes a `TypeSheet` this prop at
          // all) draws nothing extra here.
          headerRemainder={inPractice ? undefined : <PhaseClock snapshot={phaseSnapshot} variant="bare" />}
        />
      )}

      {chat !== undefined && chatSnapshot?.view === 'open' && (
        <ChatOverlay
          transcript={chatSnapshot.transcript.items}
          phase={chatPhase}
          awaitingFirstOutput={chatSnapshot.awaitingFirstOutput}
          noOutputSettle={chatSnapshot.noOutputSettle}
          statusLine={scopeName === null ? undefined : `Current selected type: ${scopeName}`}
          onSend={(text) => chat.sendAndOpen(text, scopeName)}
          onStop={() => chat.stop()}
          // ✕ and Escape MINIMISE (`player-chat-and-outcome` **D-2**) — the turn, its stream and
          // the thread all survive, and the reentry row above the prompt takes over reporting.
          // `close()` stays on the seam for a genuine dismissal (a new match, a session reset).
          onClose={() => chat.minimise()}
          // "Refresh conversation" (`beekeeper-progress-indicator` §1/§2): the ONLY way `uncertain`
          // resolves — evidence via a rehydrate, never a timer and never an inferred outcome.
          onRefresh={() => void chat.rehydrate()}
          // "New chat" (WAG-94), passed only when the channel can actually clear — a seam without
          // `clearConversation` renders no such control at all (`ChatSeam.clearConversation`).
          {...(chat.clearConversation === undefined
            ? {}
            : { onClear: () => void chat.clearConversation!() })}
          // Same mounting as the type sheet's, beside the existing Stop/Close control
          // (`prototype/sketches/rounds-client/phase-clock-slot.html`'s option D).
          headerRemainder={inPractice ? undefined : <PhaseClock snapshot={phaseSnapshot} variant="bare" />}
        />
      )}

      {/* The rounds first-run guide (WAG-11, design D-Guide's rounds counterpart): a fixed
          full-screen layer at z-index 40 — above the sheet/selection popups (20/30/10, this
          screen's control stack itself is unpositioned) and below `ChatOverlay` (50, which
          `CONTRACT.md` §7 already keeps above 40 for exactly this reason). Its step is the
          store-held `guideStep` `Colony.tsx` also drives, so it survives this screen's own
          remounts. */}
      {showRoundsGuide && (
        <FirstRunGuide
          step={guideStep}
          onNext={() => useClientStore.getState().setGuideStep(guideStep + 1)}
          onBack={() => useClientStore.getState().setGuideStep(guideStep - 1)}
          onDismiss={dismissRoundsGuide}
          steps={ROUNDS_GUIDE_STEPS}
        />
      )}
    </div>
  );
}

/** Why the world view is empty, in the words the coordinator's own state carries. Never a spinner
 * over a state that is not loading. */
function emptyWorldCopy(state: SandboxState): string {
  switch (state.world.kind) {
    case 'loading':
      return 'Loading this run…';
    case 'loaded':
    case 'in-flight':
      // Unreachable: both arms carry a trace, so the world draws a frame rather than copy. Stated
      // rather than thrown — an exhaustive switch that cannot lie is worth more here than an
      // assertion that can.
      return '';
    case 'empty':
      switch (state.world.reason) {
        case 'no-selection':
          return 'Pick a version to watch its run, or press Run to simulate this one.';
        case 'not-run':
          return 'This version has not been run yet.';
        case 'load-failed':
          return 'This run could not be loaded.';
      }
  }
}

/**
 * The practice identifier, in the slot a tournament's phase clock occupies (`client-practice` "The
 * vacated slot names the session"; sketch `practice-framing.html` panel B).
 *
 * A **statement, not a countdown**: there is no clock, no round and nothing counting down, so this
 * carries the session's identity and its frozen configuration and nothing that could be mistaken for
 * a deadline. `role="status"` rather than a bare label, so a screen reader reaching the corner is
 * told which kind of session this is.
 */
/**
 * A run in flight, as a determinate position. Never a busy state: a local run knows its own horizon
 * (`rounds-local-run` "A run reports determinate progress"), so an indeterminate readout would be
 * withholding a number the client is holding.
 */
interface PracticeProgressView {
  readonly ticksObserved: number;
  readonly terminalTick: number;
  /** The sentence, already composed against the run's own tick rate. */
  readonly label: string;
}

function PracticeTag({
  framing,
  progress = null,
  style,
}: {
  framing: SandboxPracticeFraming;
  progress?: PracticeProgressView | null;
  style?: CSSProperties;
}) {
  return (
    <div
      data-testid="sandbox-practice-tag"
      role="status"
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 2,
        minHeight: 34,
        padding: '4px 11px',
        borderRadius: 'var(--r-pill)',
        background: 'var(--hud-bg)',
        border: '1.5px solid var(--hud-line)',
        boxShadow: 'var(--shadow-hud)',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      <span
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 600,
          fontSize: 11,
          letterSpacing: 'var(--tracking-caps)',
          textTransform: 'uppercase',
          color: 'var(--hud-text)',
          lineHeight: 1,
        }}
      >
        {framing.label}
      </span>
      {framing.detail !== null && (
        <span
          data-testid="sandbox-practice-tag-detail"
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            color: 'var(--hud-text-dim)',
            lineHeight: 1.2,
          }}
        >
          {framing.detail}
        </span>
      )}
      {/* The determinate run statement. `role="progressbar"` with real bounds rather than a
          spinner: the numbers exist, so withholding them and drawing a rotating dot instead is a
          choice to be less honest than the client can afford to be. Absent entirely when no run is
          in flight — a readout pinned at `0:00` reads as a stalled run. */}
      {progress !== null && (
        <span
          data-testid="sandbox-practice-progress"
          role="progressbar"
          data-determinate="true"
          aria-valuemin={0}
          aria-valuenow={progress.ticksObserved}
          aria-valuemax={progress.terminalTick}
          aria-valuetext={progress.label}
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 10,
            color: 'var(--hud-text)',
            lineHeight: 1.3,
            maxWidth: 168,
            whiteSpace: 'normal',
          }}
        >
          {progress.label}
        </span>
      )}
    </div>
  );
}

function SubmittedSetBanner({
  label,
  editorAhead,
  top,
  height,
}: {
  label: string;
  editorAhead: boolean;
  top: number;
  height: number;
}) {
  return (
    <div
      data-testid="sandbox-submitted-set"
      style={{
        position: 'absolute',
        top,
        left: 12,
        right: 12,
        minHeight: height,
        boxSizing: 'border-box',
        padding: '7px 10px',
        borderRadius: 'var(--r-md)',
        background: 'var(--hud-bg)',
        border: '1px solid var(--hud-line)',
        fontFamily: 'var(--font-body)',
        fontSize: 11,
        color: 'var(--ink-2)',
        zIndex: 2,
      }}
    >
      <b>Submitted: {label}</b>
      <span>{editorAhead ? ' — the editor has moved on from this submission.' : ' — separate from the editor.'}</span>
    </div>
  );
}

function CandidateSelectionSurface({
  candidates,
  affordance,
  bottom,
  disabled,
  disabledReason,
  onChoose,
}: {
  candidates: readonly SandboxSelectionCandidate[];
  affordance: Exclude<SelectionAffordance, { visible: false }>;
  /** The live stack height, not a constant: the prompt bar grows a thread row when a chat exists,
   * and a hard-coded bottom put this card over the transport the moment it did. */
  bottom: number;
  disabled: boolean;
  disabledReason: string | null;
  onChoose(candidate: SandboxSelectionCandidate): void;
}) {
  const unavailable = disabled || !affordance.enabled;
  return (
    <section
      data-testid="sandbox-candidate-selection"
      aria-label="Choose a candidate for this round"
      style={{
        position: 'absolute',
        zIndex: 10,
        left: '50%',
        width: 'calc(100% - 24px)',
        maxWidth: SANDBOX_TRANSIENT_CARD_MAX_WIDTH,
        boxSizing: 'border-box',
        transform: 'translateX(-50%)',
        bottom,
        maxHeight: 260,
        display: 'flex',
        flexDirection: 'column',
        padding: 8,
        borderRadius: 'var(--r-md)',
        background: 'var(--hud-bg)',
        border: '1px solid var(--hud-line)',
        // WAG-25: the card is already centred with a `maxWidth` and its `bottom` leaves the field
        // reachable either side of it, but the card's own footprint — including its transparent
        // padding — still swallowed canvas pan/zoom underneath it. `none` here restores that, and
        // the two interactive children below opt back in with `auto` so the heading stays readable
        // and every row stays clickable.
        pointerEvents: 'none',
      }}
    >
      {/* **The picker's missing context** (UI/UX audit — this shipped as bare, floating "Candidate
          1..4 ✓" rows with no heading to say what choosing one *does*). Compact by design: one
          bold line naming the act, one muted line naming its consequence, nothing else — the rows
          below are still the surface's whole content, and this must not eat into the modest height
          budget a HUD popup over the world gets. Outside the scrolling rows list, so it never
          scrolls out of view itself. */}
      <header
        style={{
          flex: 'none',
          padding: '2px 2px 7px',
          marginBottom: 5,
          borderBottom: '1px solid var(--line)',
          pointerEvents: 'auto',
        }}
      >
        <p
          data-testid="sandbox-candidate-selection-heading"
          style={{ margin: 0, fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 13, color: 'var(--ink-1)' }}
        >
          Pick the run to submit for grading
        </p>
        <p
          data-testid="sandbox-candidate-selection-subheading"
          style={{ margin: '2px 0 0', fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--ink-3)' }}
        >
          The selected run is what this round scores.
        </p>
      </header>
      <div style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', pointerEvents: 'auto' }}>
        {unavailable && <p data-testid="sandbox-selection-disabled-reason" style={{ margin: '0 0 6px', fontSize: 12 }}>{disabledReason}</p>}
        {candidates.map((candidate) => (
          <div
            key={candidate.candidateId}
            data-testid={`sandbox-selection-candidate-${candidate.candidateId}`}
            style={{ minHeight: 'var(--hit-target)', display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <span style={{ flex: 1, fontFamily: 'var(--font-body)', fontSize: 13 }}>
              Candidate {candidate.candidateId}
              {candidate.label ? ` · ${candidate.label}` : ''}
              {candidate.botOpponents ? ' · bots on' : ''}
            </span>
            {/* **UI/UX audit F-03.** This shipped as a bare `<button>` with no styling at all: the UA
                default grey fill, a `2px outset` bevel and square corners — measured
                `background-color: rgb(240,240,240)`, `border: 2px outset rgb(0,0,0)`,
                `border-radius: 0px` — on the control that chooses which version is submitted for
                grading. Its `aria-label` and 44×44 box were already right; only the visual treatment
                was missing, which is why the finding is Medium rather than High.

                Routed through the kit's `Button` at its new `size="icon"` (F-03's prerequisite): 'md'
                carries `padding: 0 20px`, which would have made a single-glyph label a 84px-wide box
                rather than the 44×44 square this row expects. */}
            <Button
              variant="secondary"
              size="icon"
              data-testid={`sandbox-select-candidate-${candidate.candidateId}`}
              aria-label={`Select candidate ${candidate.candidateId}`}
              disabled={unavailable}
              onClick={() => onChoose(candidate)}
            >
              <span aria-hidden>✓</span>
            </Button>
          </div>
        ))}
      </div>
    </section>
  );
}

function SelectionConfirmation({
  candidate,
  copy,
  pending,
  onCancel,
  onConfirm,
}: {
  candidate: SandboxSelectionCandidate;
  copy: string;
  pending: boolean;
  onCancel(): void;
  onConfirm(): void;
}) {
  return (
    <div
      data-testid="sandbox-selection-confirmation"
      role="dialog"
      aria-label={`Confirm candidate ${candidate.candidateId}`}
      style={{
        position: 'absolute',
        zIndex: 30,
        left: '50%',
        width: 'calc(100% - 40px)',
        maxWidth: SANDBOX_TRANSIENT_CARD_MAX_WIDTH,
        boxSizing: 'border-box',
        transform: 'translateX(-50%)',
        top: 100,
        padding: 16,
        borderRadius: 'var(--r-lg)',
        background: 'var(--surface-app)',
        border: '1.5px solid var(--hud-line)',
        boxShadow: 'var(--shadow-sheet)',
      }}
    >
      <p style={{ margin: '0 0 12px' }}>Candidate {candidate.candidateId}. {copy}</p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button type="button" data-testid="sandbox-selection-cancel" disabled={pending} onClick={onCancel}>Cancel</button>
        <button type="button" data-testid="sandbox-selection-confirm" disabled={pending} onClick={onConfirm}>
          {pending ? 'Selecting…' : 'Confirm selection'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------
// The counter stack
// ---------------------------------------------------------------------------------------

interface CounterStackProps {
  /** Living, non-queen bees at the playhead — `null` where no record is resident. */
  population: number | null;
  /** Stored nectar at the playhead — `null` where no record is resident. */
  nectar: number | null;
  /** The colony's stores ceiling at the same playhead (WAG-100), or `null` where the run
   * declares no per-cell capacity to derive one from. */
  nectarCapacity: number | null;
  run: ReviewRun | null;
  previousScore: number | null;
  infoOpen: boolean;
  onToggleInfo(): void;
  /** The stack's `top` offset — `sandbox-layout.ts`'s `sandboxHudLayout()` (design **D9**), so
   * this is the one place the number is written down. Defaults to the ordinary, no-banner value
   * so every existing caller is unaffected. */
  top?: number;
}

/**
 * Bees and nectar read the **playhead**; the score reads the **run**. They are different
 * quantities and coincide only at the terminal tick, so they are labelled apart and separated by a
 * rule that marks exactly where the sourcing changes (visual spec §2.2).
 */
function CounterStack({
  population,
  nectar,
  nectarCapacity,
  run,
  previousScore,
  infoOpen,
  onToggleInfo,
  top = COUNTER_STACK_TOP_PX,
}: CounterStackProps) {
  // Whether the nectar pill reads as a fraction at all: both halves have to be real numbers, and
  // `ResourceCounter` treats a non-finite value as an absence, so this asks exactly what it will.
  const nectarFraction = nectar !== null && Number.isFinite(nectar) && nectarCapacity !== null;
  return (
    <div
      data-testid="sandbox-counters"
      style={{
        position: 'absolute',
        top,
        right: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        alignItems: 'flex-end',
      }}
    >
      <div role="group" data-testid="sandbox-counter-bees" aria-label={COUNTER_LABELS.bees}>
        <ResourceCounter icon={HUD_ICONS.worker} value={population} label="bees" />
      </div>
      {/* The stores and their ceiling are one reading, so they share one pill (WAG-100), and the
          `nectar` word is what the fraction displaces — the group's label carries the quantity for
          a screen reader, and the honey drop carries it on screen. Both facts are decided **here**,
          from whether a fraction will actually be drawn, so a pill that falls back to its
          placeholder keeps its unit word instead of standing wordless. */}
      <div
        role="group"
        data-testid="sandbox-counter-nectar"
        aria-label={nectarFraction ? COUNTER_LABELS.nectarOfCapacity : COUNTER_LABELS.nectar}
      >
        <ResourceCounter
          icon={HUD_ICONS.nectar}
          value={nectar}
          capacity={nectarFraction ? nectarCapacity : null}
          label={nectarFraction ? undefined : 'nectar'}
        />
      </div>

      {run !== null && (
        <>
          <div
            data-testid="sandbox-counter-rule"
            style={{
              width: COUNTER_PILL_WIDTH,
              height: 1,
              margin: '1px 0',
              background: 'rgba(255, 253, 246, 0.55)',
            }}
          />
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 4 }}
          >
            <button
              type="button"
              data-testid="sandbox-score-info"
              aria-label={SCORE_HEADING}
              aria-expanded={infoOpen}
              onClick={onToggleInfo}
              style={{
                // 44 × 44 rather than the kit's 18 × 18 — see the file header's departure 1.
                width: 'var(--hit-target)',
                height: 'var(--hit-target)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 'none',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 9,
                  border: '1px solid var(--line-strong)',
                  background: 'var(--paper-1)',
                  color: 'var(--ink-3)',
                  fontSize: 10,
                  fontWeight: 700,
                  lineHeight: '16px',
                  textAlign: 'center',
                }}
              >
                i
              </span>
            </button>
            <div role="group" data-testid="sandbox-counter-score" aria-label={COUNTER_LABELS.score}>
              <ResourceCounter
                icon={<span aria-hidden="true" style={{ width: 18, textAlign: 'center', fontSize: 14 }}>★</span>}
                value={run.score}
                label="score"
                delta={scoreComparison(run.score, previousScore)}
              />
            </div>
          </div>

          {infoOpen && <ScorePopover run={run} />}
        </>
      )}
    </div>
  );
}

/** The score's supporting evidence and its **required** not-a-forecast statement. Every figure is
 * derived by `review-model.ts`; nothing here composes a number of its own. */
/** The sandbox composition comes from the produced run, never the word “sandbox”: a rehearsal may
 * include bot colonies. The graded comparison is always human-contested on a fresh hidden seed. */
function sandboxForecastCaption(run: ReviewRun): string {
  const sandboxComposition =
    run.colonyCount <= 1
      ? 'This sandbox run was your colony alone'
      : `This sandbox run was against ${run.colonyCount - 1} bot ${run.colonyCount === 2 ? 'colony' : 'colonies'}`;
  return `${sandboxComposition}; a graded round is contested on a different, hidden seed, so this is evidence about how your bees behave, not a predicted placing.`;
}

function ScorePopover({ run }: { run: ReviewRun }) {
  return (
    <div
      data-testid="sandbox-score-popover"
      style={{
        width: 232,
        padding: '10px 12px',
        borderRadius: 'var(--r-md)',
        background: 'var(--hud-bg)',
        border: '1.5px solid var(--hud-line)',
        boxShadow: 'var(--shadow-hud)',
        textAlign: 'left',
      }}
    >
      <h5
        style={{
          margin: '0 0 6px',
          fontFamily: 'var(--font-body)',
          fontWeight: 800,
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-caps)',
          color: 'var(--text-muted)',
        }}
      >
        {SCORE_HEADING}
      </h5>
      {scoreStatRows(run).map((row) => (
        <div
          key={row.label}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 8,
            padding: '2px 0',
            fontFamily: 'var(--font-body)',
            fontSize: 12,
            color: 'var(--ink-2)',
          }}
        >
          <span>{row.label}</span>
          <b style={{ fontFamily: 'var(--font-display)', fontWeight: 600, color: 'var(--ink-1)' }}>
            {row.value}
          </b>
        </div>
      ))}
      <p
        data-testid="sandbox-not-a-forecast"
        style={{
          margin: '8px 0 0',
          paddingTop: 8,
          borderTop: '1px solid var(--line)',
          fontFamily: 'var(--font-body)',
          fontSize: 11,
          lineHeight: 1.45,
          color: 'var(--text-muted)',
        }}
      >
        {sandboxForecastCaption(run)}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------------------
// The waiting states and the G9 defaulting notice
//
// Both are **cards over the live screen, never a takeover** (`prototype/sketches/rounds-client/
// waiting-states.html`): the world view keeps drawing behind them, which is the structural half of
// "never mistaken for a hang" — the screen is visibly not frozen even before the copy is read.
// Neither uses `role="alert"` or `--danger`: nothing here has failed, and `waiting-state.ts`'s own
// type has nowhere to put a tone that could say otherwise.
// ---------------------------------------------------------------------------------------

const WAITING_CARD_STYLE: CSSProperties = {
  position: 'absolute',
  left: '50%',
  width: 'calc(100% - 32px)',
  maxWidth: SANDBOX_TRANSIENT_CARD_MAX_WIDTH,
  boxSizing: 'border-box',
  top: '46%',
  transform: 'translate(-50%, -50%)',
  padding: '14px 15px 13px',
  borderRadius: 'var(--r-lg)',
  background: 'var(--hud-bg)',
  border: '1.5px solid var(--hud-line)',
  boxShadow: 'var(--shadow-sheet)',
};

const WAITING_CARD_TITLE_STYLE: CSSProperties = {
  margin: '0 0 5px',
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  fontSize: 19,
  lineHeight: 1.2,
  color: 'var(--ink-1)',
};

const WAITING_CARD_BODY_STYLE: CSSProperties = {
  margin: 0,
  fontSize: 12,
  lineHeight: 1.5,
  color: 'var(--ink-2)',
};

/** One waiting state's copy, or `null` for `{kind: 'none'}` — there is nothing to render. */
function waitingCardCopy(
  state: WaitingState,
): { readonly testId: string; readonly title: string; readonly body: string } | null {
  switch (state.kind) {
    case 'none':
      return null;
    case 'awaiting-start':
      // No clock, no countdown, no zero (`client-rounds-phase`): the join window has no deadline
      // at all, so the body states plainly that nothing is running yet rather than leaving a
      // stopped-looking phase clock to imply one. The player's own name and ordinal are the one
      // thing worth confirming here — it is the answer to "did my join work".
      return {
        testId: 'sandbox-waiting-awaiting-start',
        title: 'Waiting for the presenter to start',
        body: `You’re in as ${state.displayName === '' ? 'you' : state.displayName} — ${ordinal(state.joinOrdinal)} to join. Nothing is running yet; the round begins the moment the presenter presses Start.`,
      };
    case 'nothing-run-yet':
      return {
        testId: 'sandbox-waiting-nothing-run',
        title: 'Nothing has been run yet',
        body: 'Press Run to simulate your colony’s scripts and watch what they do — you can run as many times as you like.',
      };
    case 'waiting-on-others': {
      const n = state.outstanding;
      return {
        testId: 'sandbox-waiting-others',
        title: 'Your set is in',
        body: `Waiting on ${n} other ${n === 1 ? 'player' : 'players'} to submit for this round. You can keep watching and keep editing until the phase closes.`,
      };
    }
    case 'simulating':
      return {
        testId: 'sandbox-waiting-simulating',
        title: state.round === null ? 'This round is running' : `Round ${state.round} is running`,
        body: 'The result lands here when it finishes.',
      };
    case 'reviewing':
      // Review has a dedicated card below because it carries a scored result and an explicit
      // navigation action. It is intentionally not a generic waiting message.
      return null;
  }
}

/** Renders one of the three named waiting states, or nothing. */
function WaitingCard({ state }: { state: WaitingState }) {
  const copy = waitingCardCopy(state);
  if (copy === null) {
    return null;
  }
  return (
    <div data-testid={copy.testId} role="status" style={WAITING_CARD_STYLE}>
      <h4 style={WAITING_CARD_TITLE_STYLE}>{copy.title}</h4>
      <p style={WAITING_CARD_BODY_STYLE}>{copy.body}</p>
    </div>
  );
}

function ordinal(value: number): string {
  const lastTwo = value % 100;
  if (lastTwo >= 11 && lastTwo <= 13) return `${value}th`;
  switch (value % 10) {
    case 1:
      return `${value}st`;
    case 2:
      return `${value}nd`;
    case 3:
      return `${value}rd`;
    default:
      return `${value}th`;
  }
}

/**
 * The review-phase result is a passive card until the player presses Watch. The composition root
 * supplies the already-derived D4 result; keeping this component callback-only makes a phase
 * transition and a standings-read arrival structurally incapable of navigating by themselves.
 */
function ReviewResultCard({
  result,
  round,
  onWatch,
}: {
  result: SandboxReviewResult | null;
  round: number | null;
  onWatch?: (round: number) => void;
}) {
  // **Independent of whether the D4 result has landed** (adversarial review F4). `round` comes
  // straight from the phase snapshot — known the instant the phase itself is known, never gated on
  // the standings read `result` depends on — so a transient standings failure, or a `/colony` load
  // that lands in Standings before that read resolves, must not withhold the one thing that gets
  // the player back to their own graded round. `result === null` already renders "Reading your
  // scored result…" honestly in its place; Watch stays reachable throughout.
  const usable = round !== null && onWatch !== undefined;
  const targetRound = result?.round ?? round;
  return (
    <div data-testid="sandbox-review-result" role="status" style={WAITING_CARD_STYLE}>
      <h4 style={WAITING_CARD_TITLE_STYLE}>
        {targetRound === null ? 'Round result' : `Round ${targetRound} result`}
      </h4>
      {result === null ? (
        <p style={WAITING_CARD_BODY_STYLE}>Reading your scored result…</p>
      ) : (
        <>
          <p style={WAITING_CARD_BODY_STYLE}>
            {result.placement === null
              ? 'Your placement is not available yet.'
              : `${result.shared ? 'Shared ' : ''}${ordinal(result.placement)} place`}
            {result.score === null ? '' : ` · score ${result.score}`}
          </p>
          {result.detailEvicted && (
            <p style={{ ...WAITING_CARD_BODY_STYLE, marginTop: 7 }}>
              Close detail has been reclaimed; this round is still available in coarse playback.
            </p>
          )}
        </>
      )}
      {usable && targetRound !== null && (
        <button
          type="button"
          data-testid="sandbox-watch-graded-round"
          onClick={() => onWatch(targetRound)}
          style={{
            marginTop: 12,
            minWidth: 'var(--hit-target)',
            height: 'var(--hit-target)',
            padding: '0 14px',
            borderRadius: 'var(--r-pill)',
            border: '1px solid var(--accent-strong)',
            background: 'var(--accent-fill)',
            color: 'var(--surface-card)',
            fontFamily: 'var(--font-display)',
            cursor: 'pointer',
          }}
        >
          Watch round
        </button>
      )}
    </div>
  );
}

/** `defaulted_preset`/`defaulted_previous` are the only provenances G9's notice binds to
 * (`net/rounds-http.ts`'s own doc comment on `SubmissionReadDto.provenance`) — never the local
 * countdown, and never `'explicit'`. */
function isDefaultedProvenance(provenance: string | null): boolean {
  return provenance === 'defaulted_preset' || provenance === 'defaulted_previous';
}

/** `"run:11"` → `"Run 11"`. Falls back to the raw ref for a shape this build does not recognize,
 * rather than inventing a number. */
function candidateRefLabel(candidateRef: string): string {
  const match = /^run:(\d+)$/.exec(candidateRef);
  return match === null ? candidateRef : `Run ${match[1]}`;
}

/**
 * The G9 defaulting notice. All three facts are named, numbered rather than buried in a paragraph
 * (`client-rounds-phase`'s requirement, `waiting-states.html` panel 4): which set was submitted,
 * that it was defaulted rather than chosen, and that the last edit was kept and carries forward.
 * Honey border, never `--danger` — the tournament did what it promised.
 */
function DefaultingNoticeCard({
  submission,
  onDismiss,
}: {
  submission: SubmissionReadDto;
  onDismiss(): void;
}) {
  const candidate = candidateRefLabel(submission.candidateRef);
  return (
    <div
      data-testid="sandbox-defaulting-notice"
      role="status"
      style={{ ...WAITING_CARD_STYLE, borderColor: 'var(--honey-600)' }}
    >
      <h4 style={WAITING_CARD_TITLE_STYLE}>Your set was submitted for you</h4>
      <p style={WAITING_CARD_BODY_STYLE}>
        You had not marked a set for this round, so the tournament submitted one on your behalf.
      </p>
      <ol
        style={{
          margin: '11px 0 0',
          padding: 0,
          listStyle: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 7,
        }}
      >
        <li style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--ink-2)' }}>
          <b style={{ fontFamily: 'var(--font-display)', fontWeight: 600, color: 'var(--ink-1)' }}>
            {candidate}
          </b>{' '}
          was submitted — it was your set for this round.
        </li>
        <li style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--ink-2)' }}>
          It was <b style={{ color: 'var(--ink-1)' }}>defaulted, not chosen</b> — the editing phase
          closed before you confirmed one.
        </li>
        <li style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--ink-2)' }}>
          Your <b style={{ color: 'var(--ink-1)' }}>last edit was kept</b> and carries into the next
          round.
        </li>
      </ol>
      <button
        type="button"
        data-testid="sandbox-defaulting-notice-dismiss"
        onClick={onDismiss}
        style={{
          marginTop: 13,
          height: 'var(--hit-target)',
          padding: '0 20px',
          borderRadius: 'var(--r-pill)',
          border: '1px solid var(--accent-strong)',
          background: 'var(--accent-fill)',
          color: '#FFFDF6',
          fontFamily: 'var(--font-display)',
          fontWeight: 500,
          fontSize: 13.5,
          cursor: 'pointer',
        }}
      >
        Got it
      </button>
    </div>
  );
}
